/**
 * TYT Platform API
 * This is the TYT Platform API. It is used by TYT's apps to interact 
 * with the underlying platform. 
 *
 * NOTE: This file is auto generated. 
 * Do not edit this file manually.
 */


import { Inject, Injectable, Optional } from '@angular/core';
import { HttpClient, HttpHeaders, HttpResponse, 
         HttpErrorResponse } from '@angular/common/http';
import { Observable, throwError } from 'rxjs';
import { catchError, map } from 'rxjs/operators';

import * as models from '../model/models';
import { COLLECTION_FORMATS } from '../variables';
import { ApiConfiguration } from '../configuration';
import { PagedArray, LoggerService } from '../../common';

/* tslint:disable:no-unused-variable member-ordering */


@Injectable()
export class UsersApi {
    protected basePath = '';
    public defaultHeaders: Record<string,string> = {};
    public configuration: ApiConfiguration = new ApiConfiguration();

    constructor(
        protected http: HttpClient, 
        @Optional() configuration: ApiConfiguration,
        protected logger: LoggerService
    ) {
        if (configuration) {
            this.configuration = configuration;
        }

        if (configuration && configuration.basePath)
            this.basePath = configuration.basePath;
    }

    private prepareResult<T>(result : Observable<HttpResponse<T>>) {
        return result.pipe(
            catchError(e => { // e has keys like _body,status,ok,statusText,headers,type,url
                if (e.status === 0 || e.status === 500 || e.status === 504) {
                    return throwError({
                        message: 'A network error has occurred.',
                        requestDetails: {
                            status: e.status,
                            statusText: e.statusText,
                            url: e.url,
                            headers: e.headers
                        },
                        error: true, 
                        errorType: 'network-error', 
                        json: function() { return this; }
                    });
                }
                return throwError(e);
            }),
            map(response => {
                if (response.status === 204)
                    return undefined;
                
                let result : any = response.body;

                if (typeof result === 'object') {
                    if (response.headers.get('X-TYT-Authentication'))
                        result['x-tyt-authentication'] = response.headers.get('X-TYT-Authentication')

                    if (result instanceof Array) {
                        if (response.headers.get('X-Total'))
                            result['total'] = Number(response.headers.get('X-Total'));
                        else if (response.headers.get('X-TYT-Total'))
                            result['total'] = Number(response.headers.get('X-TYT-Total'));
                    }
                }
                
                return result;
            })
        );
    }

    /**
     * Check if user should login by email
     * @param body 
     */
    public emailConflict(body?: models.ApiPostUsersEmailConflict, __unused?: any): Observable<{}> {
        return this.prepareResult(this.emailConflictRequest(body, __unused));
    }

    /**
     * Get the honor roll list
     */
    public getHonorRoll(__unused?: any): Observable<PagedArray<models.ApiHonorRoll>> {
        return this.prepareResult(this.getHonorRollRequest(__unused));
    }

    /**
     * Determine if a person is eligible to be a gift recipient
     * @param email 
     */
    public giftEligibleByEmail(email?: string, __unused?: any): Observable<models.ApiGiftEligibility> {
        return this.prepareResult(this.giftEligibleByEmailRequest(email, __unused));
    }

    /**
     * Sign up email address with newletters for email
     * @param body 
     */
    public signupNewsletter(body?: models.ApiPostUsersSignupNewsletter, __unused?: any): Observable<{}> {
        return this.prepareResult(this.signupNewsletterRequest(body, __unused));
    }

    /**
     * Sign up for a TYT account
     * @param body 
     */
    public signup(body?: models.ApiPostUsersSignup, __unused?: any): Observable<models.ApiUserAuthToken> {
        return this.prepareResult(this.signupRequest(body, __unused));
    }

    /**
     * Confirm email for a user
     * @param body 
     */
    public confirmEmail(body?: models.ApiPostUsersConfirmemail, __unused?: any): Observable<models.ApiUserAuthToken> {
        return this.prepareResult(this.confirmEmailRequest(body, __unused));
    }

    /**
     * Resend confirmation email Sends no indication of success. App should assume it was successful if there was indeed an account with the given email address.
     * @param body 
     */
    public sendConfirmationEmail(body?: models.ApiPostUsersSendconfirmationemail, __unused?: any): Observable<{}> {
        return this.prepareResult(this.sendConfirmationEmailRequest(body, __unused));
    }

    /**
     * Fulfill an account unlock request
     * @param body 
     */
    public unlockAccount(body?: models.ApiPostUsersUnlockaccount, __unused?: any): Observable<{}> {
        return this.prepareResult(this.unlockAccountRequest(body, __unused));
    }

    /**
     * Send an email to unlock account Returns no indication of success. App should assume that email was sent provided an account existed with that address.
     * @param body 
     */
    public requestAccountUnlock(body?: models.ApiPostUsersRequestunlock, __unused?: any): Observable<{}> {
        return this.prepareResult(this.requestAccountUnlockRequest(body, __unused));
    }

    /**
     * Fulfill a password reset request
     * @param body 
     */
    public resetPassword(body?: models.ApiPostUsersResetpassword, __unused?: any): Observable<models.ApiUserAuthToken> {
        return this.prepareResult(this.resetPasswordRequest(body, __unused));
    }

    /**
     * Assume a user by token Must pass Platform session cookie. Platform Assume User feature must have been used within that session prior to this API call.
     */
    public assume(__unused?: any): Observable<models.ApiUserAuthToken> {
        return this.prepareResult(this.assumeRequest(__unused));
    }

    /**
     * Send a password reset request Returns no indication of success. App should assume that reset email was sent if an account existed, otherwise it was not sent.
     * @param body 
     */
    public requestPasswordReset(body?: models.ApiPostUsersRequestpasswordreset, __unused?: any): Observable<{}> {
        return this.prepareResult(this.requestPasswordResetRequest(body, __unused));
    }

    /**
     * Authenticate using twitter, facebook, or google oauth credentials Requires at least a token. Twitter also requires a secret (shh).
     * @param body 
     */
    public userOmniauthAuth(body?: models.ApiPostUsersOmniauth, __unused?: any): Observable<models.ApiUserAuthToken> {
        return this.prepareResult(this.userOmniauthAuthRequest(body, __unused));
    }

    /**
     * Authenticate as a user to obtain an auth token
     * @param body 
     */
    public authenticate(body?: models.ApiPostUsersAuth, __unused?: any): Observable<models.ApiUserAuthToken> {
        return this.prepareResult(this.authenticateRequest(body, __unused));
    }

    /**
     * 
     */
    public postUsersSso(__unused?: any): Observable<{}> {
        return this.prepareResult(this.postUsersSsoRequest(__unused));
    }

    /**
     * Kick off generating a new data download archive for the authenticated user
     */
    public startDataDownload(__unused?: any): Observable<{}> {
        return this.prepareResult(this.startDataDownloadRequest(__unused));
    }

    /**
     * Get the payment source
     */
    public getPaymentSource(__unused?: any): Observable<models.ApiPaymentSource> {
        return this.prepareResult(this.getPaymentSourceRequest(__unused));
    }

    /**
     * Perform a permissions check for the current user
     * @param body 
     */
    public checkPermission(body?: models.ApiPostUsersCurrentPermissionsCheck, __unused?: any): Observable<models.ApiPermissionCheckResult> {
        return this.prepareResult(this.checkPermissionRequest(body, __unused));
    }

    /**
     * Get account transactions for the current user
     * @param page 
     */
    public getAccountTransactions(page?: number, __unused?: any): Observable<PagedArray<models.ApiAccountTransaction>> {
        return this.prepareResult(this.getAccountTransactionsRequest(page, __unused));
    }

    /**
     * Delete the current user
     * @param body 
     */
    public deleteCurrentUser(body?: models.ApiPostUsersCurrentDelete, __unused?: any): Observable<models.ApiDeletionConfirmation> {
        return this.prepareResult(this.deleteCurrentUserRequest(body, __unused));
    }

    /**
     * Get the current user
     */
    public getCurrentUser(__unused?: any): Observable<models.ApiUser> {
        return this.prepareResult(this.getCurrentUserRequest(__unused));
    }

    /**
     * Update user
     * @param body 
     */
    public updateUser(body?: models.ApiPostUsersCurrent, __unused?: any): Observable<models.ApiUser> {
        return this.prepareResult(this.updateUserRequest(body, __unused));
    }

    /**
     * Create a PaymentIntent for a contribution or subscription purchase as necessary for the given offer set
     * @param offer_set_uuid 
     * @param offer_uuid 
     */
    public getStripePaymentIntent(offer_set_uuid?: string, offer_uuid?: string, __unused?: any): Observable<models.ApiStripePaymentIntent> {
        return this.prepareResult(this.getStripePaymentIntentRequest(offer_set_uuid, offer_uuid, __unused));
    }

    /**
     * Get a RetentionOffer entity based on the offer code sent by the user
     * @param code 
     */
    public getRetentionOffer(code?: string, __unused?: any): Observable<models.ApiRetentionOfferSet> {
        return this.prepareResult(this.getRetentionOfferRequest(code, __unused));
    }

    /**
     * Reactivate a previously cancelled TYT membership
     */
    public reactivateMembership(__unused?: any): Observable<models.ApiMembership> {
        return this.prepareResult(this.reactivateMembershipRequest(__unused));
    }

    /**
     * Cancel an existing TYT membership
     * @param body 
     */
    public cancelMembership(body?: models.ApiPostUsersCurrentMembershipCancel, __unused?: any): Observable<models.ApiMembership> {
        return this.prepareResult(this.cancelMembershipRequest(body, __unused));
    }

    /**
     * Preview a product change on an existing TYT membership
     * @param body 
     */
    public previewMembershipChange(body?: models.ApiPostUsersCurrentMembershipModifyPreview, __unused?: any): Observable<models.ApiMembershipChangePreview> {
        return this.prepareResult(this.previewMembershipChangeRequest(body, __unused));
    }

    /**
     * Change the membership product on an existing TYT membership
     * @param body 
     */
    public modifyMembership(body?: models.ApiPostUsersCurrentMembershipModify, __unused?: any): Observable<models.ApiMembership> {
        return this.prepareResult(this.modifyMembershipRequest(body, __unused));
    }

    /**
     * Refresh authentication token
     * @param body 
     */
    public refreshAuthentication(body?: models.ApiPostUsersAuthRefresh, __unused?: any): Observable<models.ApiUserAuthToken> {
        return this.prepareResult(this.refreshAuthenticationRequest(body, __unused));
    }

    /**
     * Update payment source on an existing membership
     * @param body 
     */
    public changePaymentSource(body?: models.ApiPostUsersCurrentMembershipChangepayment, __unused?: any): Observable<models.ApiMembership> {
        return this.prepareResult(this.changePaymentSourceRequest(body, __unused));
    }

    /**
     * Sign up for a premium TYT membership for an existing user account
     * @param body 
     */
    public createMembership(body?: models.ApiPostUsersCurrentMembership, __unused?: any): Observable<models.ApiMembership> {
        return this.prepareResult(this.createMembershipRequest(body, __unused));
    }

    /**
     * Update your profile
     * @param body 
     */
    public updateProfile(body?: models.ApiPostUsersCurrentProfile, __unused?: any): Observable<models.ApiUserProfile> {
        return this.prepareResult(this.updateProfileRequest(body, __unused));
    }

    /**
     * Set your cover photo
     * @param body 
     */
    public updateCoverPhoto(body?: models.ApiPostUsersCurrentCoverphoto, __unused?: any): Observable<models.ApiUserProfile> {
        return this.prepareResult(this.updateCoverPhotoRequest(body, __unused));
    }

    /**
     * Set your avatar
     * @param body 
     */
    public updateAvatar(body?: models.ApiPostUsersCurrentAvatar, __unused?: any): Observable<models.ApiUserProfile> {
        return this.prepareResult(this.updateAvatarRequest(body, __unused));
    }

    /**
     * Add or update oauth credentials for the current user
     * @param body 
     */
    public upsertUserOmniauth(body?: models.ApiPostUsersCurrentOmniauth, __unused?: any): Observable<models.ApiUserOmniauth> {
        return this.prepareResult(this.upsertUserOmniauthRequest(body, __unused));
    }

    /**
     * Sign in to Spot.im
     * @param body 
     */
    public spotSSO(body?: models.ApiPostUsersCurrentSpotim, __unused?: any): Observable<models.ApiSpotSSO> {
        return this.prepareResult(this.spotSSORequest(body, __unused));
    }

    /**
     * Connect spot.IM user ID to TYT account after SSO
     * @param body 
     */
    public spotAssociateUser(body?: models.ApiPostUsersCurrentSpotimAssociate, __unused?: any): Observable<{}> {
        return this.prepareResult(this.spotAssociateUserRequest(body, __unused));
    }

    /**
     * Initiate a user account merge operation
     * @param body 
     */
    public mergeAccounts(body?: models.ApiPostUsersMerge, __unused?: any): Observable<models.ApiUserAuthToken> {
        return this.prepareResult(this.mergeAccountsRequest(body, __unused));
    }

    /**
     * Reset your password
     * @param body 
     */
    public resetMyPassword(body?: models.ApiPostUsersPasswordReset, __unused?: any): Observable<{}> {
        return this.prepareResult(this.resetMyPasswordRequest(body, __unused));
    }

    /**
     * Disconnect a connected account
     * @param id 
     */
    public disconnectExternalAccount(id?: string, __unused?: any): Observable<{}> {
        return this.prepareResult(this.disconnectExternalAccountRequest(id, __unused));
    }

    /**
     * Get a Public Profile
     * @param userid 
     */
    public getPublicProfile(userid?: string, __unused?: any): Observable<models.ApiPublicProfile> {
        return this.prepareResult(this.getPublicProfileRequest(userid, __unused));
    }

    /**
     * Report a user profile
     * @param userid 
     * @param body 
     */
    public reportProfile(userid?: string, body?: models.ApiPostUsersUseridReport, __unused?: any): Observable<{}> {
        return this.prepareResult(this.reportProfileRequest(userid, body, __unused));
    }

    /**
     * Retrieve an authentication token for testing This is only available in development environments. Returns 400 Bad Request when used in an unsupported environment.
     */
    public postUsersAuthTest(__unused?: any): Observable<{}> {
        return this.prepareResult(this.postUsersAuthTestRequest(__unused));
    }

    /**
     * Retrieve a specific user&#39;s auth token for testing (development only) This is only available in development environments. Returns 400 Bad Request when used in an unsupported environment.
     * @param id 
     */
    public postUsersAuthTestId(id?: number, __unused?: any): Observable<{}> {
        return this.prepareResult(this.postUsersAuthTestIdRequest(id, __unused));
    }

    /**
     * Resolve a spot conversation ID to the appropriate URL on TYT.com
     * @param identifier 
     */
    public resolveSpot(identifier?: number, __unused?: any): Observable<{}> {
        return this.prepareResult(this.resolveSpotRequest(identifier, __unused));
    }

    /**
     * Translate Spot.im user ID to TYT identity
     * @param userid 
     */
    public translateSpotUserID(userid?: string, __unused?: any): Observable<models.ApiSpotUserLookupResult> {
        return this.prepareResult(this.translateSpotUserIDRequest(userid, __unused));
    }

    /**
     * Get the user&#39;s partially watched content
     */
    public getOpenSessions(__unused?: any): Observable<PagedArray<models.ApiPlaybackSession>> {
        return this.prepareResult(this.getOpenSessionsRequest(__unused));
    }


    private applyGlobalHeaders(headers : Record<string,string>) {
        if (this.configuration.accessToken) {
            let accessToken = typeof this.configuration.accessToken === 'function'
                ? this.configuration.accessToken()
                : this.configuration.accessToken;
            headers['Authorization'] = 'Bearer ' + accessToken;
        }

        if (this.configuration.globalHeaders) {
            Object.keys(this.configuration.globalHeaders).forEach(key => {
                headers[key] = this.configuration.globalHeaders[key];
            })
        }
    }

    /**
     * 
     * 
     * @param body 
     */
    public emailConflictRequest(body?: models.ApiPostUsersEmailConflict, extraHttpRequestParams?: any): Observable<HttpResponse<{}>> {
        const path = this.basePath + '/users/email/conflict';

        let traceStart = Date.now();
        let queryParameters : Record<string,string> = {};

        let globalQueryParams = (this.configuration.globalQueryParams || {});
        Object.keys(globalQueryParams).forEach (x => queryParameters[x] = globalQueryParams[x]);
        
        let headers : Record<string,string> = Object.assign({}, this.defaultHeaders); 

        this.applyGlobalHeaders(headers);

        headers['Content-Type'] = 'application/json';


        let requestMethodStr = 'RequestMethod.Post'.replace(/RequestMethod\./, '').toUpperCase();
        let requestPath = path;
        let authState = '';

        if (Object.keys(queryParameters).length > 0) {
            requestPath = `${requestPath}?${Object.keys(queryParameters)
                .map(key => `${encodeURIComponent(key)}=${encodeURIComponent(queryParameters[key])}`).join('&')}`;
        }

        if (this.configuration.accessToken) 
            authState = `(authenticated) `;
        
        return this.http
            .request<any>(requestMethodStr, requestPath, { 
                observe: 'response', 
                responseType: 'json',
                headers, 
                withCredentials: this.configuration.withCredentials,
                body: body == null ? '' : JSON.stringify(body), // https://github.com/angular/angular/issues/10612
            })
            .pipe(
                catchError(e => {
                    let time = Date.now() - traceStart;

                    if (e instanceof HttpErrorResponse) {
                        let error = <HttpErrorResponse>e;
                        this.logger.info(`[API] ${requestMethodStr} ${authState}${requestPath} (${time} ms): Error: ${error.status} ${error.statusText}`);
                        e = error.error || e;
                    }

                    return throwError(e);
                }),
                map(response => {
                    let time = Date.now() - traceStart;
                    this.logger.info(`[API] ${requestMethodStr} ${authState}${requestPath} (${time} ms): Finished: ${response.status} ${response.statusText}`);

                    return response;
                })
            )
        ;
    }

    /**
     * 
     * 
     */
    public getHonorRollRequest(extraHttpRequestParams?: any): Observable<HttpResponse<PagedArray<models.ApiHonorRoll>>> {
        const path = this.basePath + '/users/honor-roll';

        let traceStart = Date.now();
        let queryParameters : Record<string,string> = {};

        let globalQueryParams = (this.configuration.globalQueryParams || {});
        Object.keys(globalQueryParams).forEach (x => queryParameters[x] = globalQueryParams[x]);
        
        let headers : Record<string,string> = Object.assign({}, this.defaultHeaders); 

        this.applyGlobalHeaders(headers);


        let requestMethodStr = 'RequestMethod.Get'.replace(/RequestMethod\./, '').toUpperCase();
        let requestPath = path;
        let authState = '';

        if (Object.keys(queryParameters).length > 0) {
            requestPath = `${requestPath}?${Object.keys(queryParameters)
                .map(key => `${encodeURIComponent(key)}=${encodeURIComponent(queryParameters[key])}`).join('&')}`;
        }

        if (this.configuration.accessToken) 
            authState = `(authenticated) `;
        
        return this.http
            .request<PagedArray<models.ApiHonorRoll>>(requestMethodStr, requestPath, { 
                observe: 'response', 
                responseType: 'json',
                headers, 
                withCredentials: this.configuration.withCredentials,
            })
            .pipe(
                catchError(e => {
                    let time = Date.now() - traceStart;

                    if (e instanceof HttpErrorResponse) {
                        let error = <HttpErrorResponse>e;
                        this.logger.info(`[API] ${requestMethodStr} ${authState}${requestPath} (${time} ms): Error: ${error.status} ${error.statusText}`);
                        e = error.error || e;
                    }

                    return throwError(e);
                }),
                map(response => {
                    let time = Date.now() - traceStart;
                    this.logger.info(`[API] ${requestMethodStr} ${authState}${requestPath} (${time} ms): Finished: ${response.status} ${response.statusText}`);

                    return response;
                })
            )
        ;
    }

    /**
     * 
     * 
     * @param email 
     */
    public giftEligibleByEmailRequest(email?: string, extraHttpRequestParams?: any): Observable<HttpResponse<models.ApiGiftEligibility>> {
        const path = this.basePath + '/users/giftEligible';

        let traceStart = Date.now();
        let queryParameters : Record<string,string> = {};

        let globalQueryParams = (this.configuration.globalQueryParams || {});
        Object.keys(globalQueryParams).forEach (x => queryParameters[x] = globalQueryParams[x]);
        
        let headers : Record<string,string> = Object.assign({}, this.defaultHeaders); 
        if (email !== undefined) {
            queryParameters['email'] = <any>email;
        }


        this.applyGlobalHeaders(headers);


        let requestMethodStr = 'RequestMethod.Get'.replace(/RequestMethod\./, '').toUpperCase();
        let requestPath = path;
        let authState = '';

        if (Object.keys(queryParameters).length > 0) {
            requestPath = `${requestPath}?${Object.keys(queryParameters)
                .map(key => `${encodeURIComponent(key)}=${encodeURIComponent(queryParameters[key])}`).join('&')}`;
        }

        if (this.configuration.accessToken) 
            authState = `(authenticated) `;
        
        return this.http
            .request<models.ApiGiftEligibility>(requestMethodStr, requestPath, { 
                observe: 'response', 
                responseType: 'json',
                headers, 
                withCredentials: this.configuration.withCredentials,
            })
            .pipe(
                catchError(e => {
                    let time = Date.now() - traceStart;

                    if (e instanceof HttpErrorResponse) {
                        let error = <HttpErrorResponse>e;
                        this.logger.info(`[API] ${requestMethodStr} ${authState}${requestPath} (${time} ms): Error: ${error.status} ${error.statusText}`);
                        e = error.error || e;
                    }

                    return throwError(e);
                }),
                map(response => {
                    let time = Date.now() - traceStart;
                    this.logger.info(`[API] ${requestMethodStr} ${authState}${requestPath} (${time} ms): Finished: ${response.status} ${response.statusText}`);

                    return response;
                })
            )
        ;
    }

    /**
     * 
     * 
     * @param body 
     */
    public signupNewsletterRequest(body?: models.ApiPostUsersSignupNewsletter, extraHttpRequestParams?: any): Observable<HttpResponse<{}>> {
        const path = this.basePath + '/users/signup/newsletter';

        let traceStart = Date.now();
        let queryParameters : Record<string,string> = {};

        let globalQueryParams = (this.configuration.globalQueryParams || {});
        Object.keys(globalQueryParams).forEach (x => queryParameters[x] = globalQueryParams[x]);
        
        let headers : Record<string,string> = Object.assign({}, this.defaultHeaders); 

        this.applyGlobalHeaders(headers);

        headers['Content-Type'] = 'application/json';


        let requestMethodStr = 'RequestMethod.Post'.replace(/RequestMethod\./, '').toUpperCase();
        let requestPath = path;
        let authState = '';

        if (Object.keys(queryParameters).length > 0) {
            requestPath = `${requestPath}?${Object.keys(queryParameters)
                .map(key => `${encodeURIComponent(key)}=${encodeURIComponent(queryParameters[key])}`).join('&')}`;
        }

        if (this.configuration.accessToken) 
            authState = `(authenticated) `;
        
        return this.http
            .request<any>(requestMethodStr, requestPath, { 
                observe: 'response', 
                responseType: 'json',
                headers, 
                withCredentials: this.configuration.withCredentials,
                body: body == null ? '' : JSON.stringify(body), // https://github.com/angular/angular/issues/10612
            })
            .pipe(
                catchError(e => {
                    let time = Date.now() - traceStart;

                    if (e instanceof HttpErrorResponse) {
                        let error = <HttpErrorResponse>e;
                        this.logger.info(`[API] ${requestMethodStr} ${authState}${requestPath} (${time} ms): Error: ${error.status} ${error.statusText}`);
                        e = error.error || e;
                    }

                    return throwError(e);
                }),
                map(response => {
                    let time = Date.now() - traceStart;
                    this.logger.info(`[API] ${requestMethodStr} ${authState}${requestPath} (${time} ms): Finished: ${response.status} ${response.statusText}`);

                    return response;
                })
            )
        ;
    }

    /**
     * 
     * 
     * @param body 
     */
    public signupRequest(body?: models.ApiPostUsersSignup, extraHttpRequestParams?: any): Observable<HttpResponse<models.ApiUserAuthToken>> {
        const path = this.basePath + '/users/signup';

        let traceStart = Date.now();
        let queryParameters : Record<string,string> = {};

        let globalQueryParams = (this.configuration.globalQueryParams || {});
        Object.keys(globalQueryParams).forEach (x => queryParameters[x] = globalQueryParams[x]);
        
        let headers : Record<string,string> = Object.assign({}, this.defaultHeaders); 

        this.applyGlobalHeaders(headers);

        headers['Content-Type'] = 'application/json';


        let requestMethodStr = 'RequestMethod.Post'.replace(/RequestMethod\./, '').toUpperCase();
        let requestPath = path;
        let authState = '';

        if (Object.keys(queryParameters).length > 0) {
            requestPath = `${requestPath}?${Object.keys(queryParameters)
                .map(key => `${encodeURIComponent(key)}=${encodeURIComponent(queryParameters[key])}`).join('&')}`;
        }

        if (this.configuration.accessToken) 
            authState = `(authenticated) `;
        
        return this.http
            .request<models.ApiUserAuthToken>(requestMethodStr, requestPath, { 
                observe: 'response', 
                responseType: 'json',
                headers, 
                withCredentials: this.configuration.withCredentials,
                body: body == null ? '' : JSON.stringify(body), // https://github.com/angular/angular/issues/10612
            })
            .pipe(
                catchError(e => {
                    let time = Date.now() - traceStart;

                    if (e instanceof HttpErrorResponse) {
                        let error = <HttpErrorResponse>e;
                        this.logger.info(`[API] ${requestMethodStr} ${authState}${requestPath} (${time} ms): Error: ${error.status} ${error.statusText}`);
                        e = error.error || e;
                    }

                    return throwError(e);
                }),
                map(response => {
                    let time = Date.now() - traceStart;
                    this.logger.info(`[API] ${requestMethodStr} ${authState}${requestPath} (${time} ms): Finished: ${response.status} ${response.statusText}`);

                    return response;
                })
            )
        ;
    }

    /**
     * 
     * 
     * @param body 
     */
    public confirmEmailRequest(body?: models.ApiPostUsersConfirmemail, extraHttpRequestParams?: any): Observable<HttpResponse<models.ApiUserAuthToken>> {
        const path = this.basePath + '/users/confirmEmail';

        let traceStart = Date.now();
        let queryParameters : Record<string,string> = {};

        let globalQueryParams = (this.configuration.globalQueryParams || {});
        Object.keys(globalQueryParams).forEach (x => queryParameters[x] = globalQueryParams[x]);
        
        let headers : Record<string,string> = Object.assign({}, this.defaultHeaders); 

        this.applyGlobalHeaders(headers);

        headers['Content-Type'] = 'application/json';


        let requestMethodStr = 'RequestMethod.Post'.replace(/RequestMethod\./, '').toUpperCase();
        let requestPath = path;
        let authState = '';

        if (Object.keys(queryParameters).length > 0) {
            requestPath = `${requestPath}?${Object.keys(queryParameters)
                .map(key => `${encodeURIComponent(key)}=${encodeURIComponent(queryParameters[key])}`).join('&')}`;
        }

        if (this.configuration.accessToken) 
            authState = `(authenticated) `;
        
        return this.http
            .request<models.ApiUserAuthToken>(requestMethodStr, requestPath, { 
                observe: 'response', 
                responseType: 'json',
                headers, 
                withCredentials: this.configuration.withCredentials,
                body: body == null ? '' : JSON.stringify(body), // https://github.com/angular/angular/issues/10612
            })
            .pipe(
                catchError(e => {
                    let time = Date.now() - traceStart;

                    if (e instanceof HttpErrorResponse) {
                        let error = <HttpErrorResponse>e;
                        this.logger.info(`[API] ${requestMethodStr} ${authState}${requestPath} (${time} ms): Error: ${error.status} ${error.statusText}`);
                        e = error.error || e;
                    }

                    return throwError(e);
                }),
                map(response => {
                    let time = Date.now() - traceStart;
                    this.logger.info(`[API] ${requestMethodStr} ${authState}${requestPath} (${time} ms): Finished: ${response.status} ${response.statusText}`);

                    return response;
                })
            )
        ;
    }

    /**
     * Resend confirmation email
     * 
     * @param body 
     */
    public sendConfirmationEmailRequest(body?: models.ApiPostUsersSendconfirmationemail, extraHttpRequestParams?: any): Observable<HttpResponse<{}>> {
        const path = this.basePath + '/users/sendConfirmationEmail';

        let traceStart = Date.now();
        let queryParameters : Record<string,string> = {};

        let globalQueryParams = (this.configuration.globalQueryParams || {});
        Object.keys(globalQueryParams).forEach (x => queryParameters[x] = globalQueryParams[x]);
        
        let headers : Record<string,string> = Object.assign({}, this.defaultHeaders); 

        this.applyGlobalHeaders(headers);

        headers['Content-Type'] = 'application/json';


        let requestMethodStr = 'RequestMethod.Post'.replace(/RequestMethod\./, '').toUpperCase();
        let requestPath = path;
        let authState = '';

        if (Object.keys(queryParameters).length > 0) {
            requestPath = `${requestPath}?${Object.keys(queryParameters)
                .map(key => `${encodeURIComponent(key)}=${encodeURIComponent(queryParameters[key])}`).join('&')}`;
        }

        if (this.configuration.accessToken) 
            authState = `(authenticated) `;
        
        return this.http
            .request<any>(requestMethodStr, requestPath, { 
                observe: 'response', 
                responseType: 'json',
                headers, 
                withCredentials: this.configuration.withCredentials,
                body: body == null ? '' : JSON.stringify(body), // https://github.com/angular/angular/issues/10612
            })
            .pipe(
                catchError(e => {
                    let time = Date.now() - traceStart;

                    if (e instanceof HttpErrorResponse) {
                        let error = <HttpErrorResponse>e;
                        this.logger.info(`[API] ${requestMethodStr} ${authState}${requestPath} (${time} ms): Error: ${error.status} ${error.statusText}`);
                        e = error.error || e;
                    }

                    return throwError(e);
                }),
                map(response => {
                    let time = Date.now() - traceStart;
                    this.logger.info(`[API] ${requestMethodStr} ${authState}${requestPath} (${time} ms): Finished: ${response.status} ${response.statusText}`);

                    return response;
                })
            )
        ;
    }

    /**
     * 
     * 
     * @param body 
     */
    public unlockAccountRequest(body?: models.ApiPostUsersUnlockaccount, extraHttpRequestParams?: any): Observable<HttpResponse<{}>> {
        const path = this.basePath + '/users/unlockAccount';

        let traceStart = Date.now();
        let queryParameters : Record<string,string> = {};

        let globalQueryParams = (this.configuration.globalQueryParams || {});
        Object.keys(globalQueryParams).forEach (x => queryParameters[x] = globalQueryParams[x]);
        
        let headers : Record<string,string> = Object.assign({}, this.defaultHeaders); 

        this.applyGlobalHeaders(headers);

        headers['Content-Type'] = 'application/json';


        let requestMethodStr = 'RequestMethod.Post'.replace(/RequestMethod\./, '').toUpperCase();
        let requestPath = path;
        let authState = '';

        if (Object.keys(queryParameters).length > 0) {
            requestPath = `${requestPath}?${Object.keys(queryParameters)
                .map(key => `${encodeURIComponent(key)}=${encodeURIComponent(queryParameters[key])}`).join('&')}`;
        }

        if (this.configuration.accessToken) 
            authState = `(authenticated) `;
        
        return this.http
            .request<any>(requestMethodStr, requestPath, { 
                observe: 'response', 
                responseType: 'json',
                headers, 
                withCredentials: this.configuration.withCredentials,
                body: body == null ? '' : JSON.stringify(body), // https://github.com/angular/angular/issues/10612
            })
            .pipe(
                catchError(e => {
                    let time = Date.now() - traceStart;

                    if (e instanceof HttpErrorResponse) {
                        let error = <HttpErrorResponse>e;
                        this.logger.info(`[API] ${requestMethodStr} ${authState}${requestPath} (${time} ms): Error: ${error.status} ${error.statusText}`);
                        e = error.error || e;
                    }

                    return throwError(e);
                }),
                map(response => {
                    let time = Date.now() - traceStart;
                    this.logger.info(`[API] ${requestMethodStr} ${authState}${requestPath} (${time} ms): Finished: ${response.status} ${response.statusText}`);

                    return response;
                })
            )
        ;
    }

    /**
     * Send an email to unlock account
     * 
     * @param body 
     */
    public requestAccountUnlockRequest(body?: models.ApiPostUsersRequestunlock, extraHttpRequestParams?: any): Observable<HttpResponse<{}>> {
        const path = this.basePath + '/users/requestUnlock';

        let traceStart = Date.now();
        let queryParameters : Record<string,string> = {};

        let globalQueryParams = (this.configuration.globalQueryParams || {});
        Object.keys(globalQueryParams).forEach (x => queryParameters[x] = globalQueryParams[x]);
        
        let headers : Record<string,string> = Object.assign({}, this.defaultHeaders); 

        this.applyGlobalHeaders(headers);

        headers['Content-Type'] = 'application/json';


        let requestMethodStr = 'RequestMethod.Post'.replace(/RequestMethod\./, '').toUpperCase();
        let requestPath = path;
        let authState = '';

        if (Object.keys(queryParameters).length > 0) {
            requestPath = `${requestPath}?${Object.keys(queryParameters)
                .map(key => `${encodeURIComponent(key)}=${encodeURIComponent(queryParameters[key])}`).join('&')}`;
        }

        if (this.configuration.accessToken) 
            authState = `(authenticated) `;
        
        return this.http
            .request<any>(requestMethodStr, requestPath, { 
                observe: 'response', 
                responseType: 'json',
                headers, 
                withCredentials: this.configuration.withCredentials,
                body: body == null ? '' : JSON.stringify(body), // https://github.com/angular/angular/issues/10612
            })
            .pipe(
                catchError(e => {
                    let time = Date.now() - traceStart;

                    if (e instanceof HttpErrorResponse) {
                        let error = <HttpErrorResponse>e;
                        this.logger.info(`[API] ${requestMethodStr} ${authState}${requestPath} (${time} ms): Error: ${error.status} ${error.statusText}`);
                        e = error.error || e;
                    }

                    return throwError(e);
                }),
                map(response => {
                    let time = Date.now() - traceStart;
                    this.logger.info(`[API] ${requestMethodStr} ${authState}${requestPath} (${time} ms): Finished: ${response.status} ${response.statusText}`);

                    return response;
                })
            )
        ;
    }

    /**
     * 
     * 
     * @param body 
     */
    public resetPasswordRequest(body?: models.ApiPostUsersResetpassword, extraHttpRequestParams?: any): Observable<HttpResponse<models.ApiUserAuthToken>> {
        const path = this.basePath + '/users/resetPassword';

        let traceStart = Date.now();
        let queryParameters : Record<string,string> = {};

        let globalQueryParams = (this.configuration.globalQueryParams || {});
        Object.keys(globalQueryParams).forEach (x => queryParameters[x] = globalQueryParams[x]);
        
        let headers : Record<string,string> = Object.assign({}, this.defaultHeaders); 

        this.applyGlobalHeaders(headers);

        headers['Content-Type'] = 'application/json';


        let requestMethodStr = 'RequestMethod.Post'.replace(/RequestMethod\./, '').toUpperCase();
        let requestPath = path;
        let authState = '';

        if (Object.keys(queryParameters).length > 0) {
            requestPath = `${requestPath}?${Object.keys(queryParameters)
                .map(key => `${encodeURIComponent(key)}=${encodeURIComponent(queryParameters[key])}`).join('&')}`;
        }

        if (this.configuration.accessToken) 
            authState = `(authenticated) `;
        
        return this.http
            .request<models.ApiUserAuthToken>(requestMethodStr, requestPath, { 
                observe: 'response', 
                responseType: 'json',
                headers, 
                withCredentials: this.configuration.withCredentials,
                body: body == null ? '' : JSON.stringify(body), // https://github.com/angular/angular/issues/10612
            })
            .pipe(
                catchError(e => {
                    let time = Date.now() - traceStart;

                    if (e instanceof HttpErrorResponse) {
                        let error = <HttpErrorResponse>e;
                        this.logger.info(`[API] ${requestMethodStr} ${authState}${requestPath} (${time} ms): Error: ${error.status} ${error.statusText}`);
                        e = error.error || e;
                    }

                    return throwError(e);
                }),
                map(response => {
                    let time = Date.now() - traceStart;
                    this.logger.info(`[API] ${requestMethodStr} ${authState}${requestPath} (${time} ms): Finished: ${response.status} ${response.statusText}`);

                    return response;
                })
            )
        ;
    }

    /**
     * Assume a user by token
     * 
     */
    public assumeRequest(extraHttpRequestParams?: any): Observable<HttpResponse<models.ApiUserAuthToken>> {
        const path = this.basePath + '/users/assume';

        let traceStart = Date.now();
        let queryParameters : Record<string,string> = {};

        let globalQueryParams = (this.configuration.globalQueryParams || {});
        Object.keys(globalQueryParams).forEach (x => queryParameters[x] = globalQueryParams[x]);
        
        let headers : Record<string,string> = Object.assign({}, this.defaultHeaders); 

        this.applyGlobalHeaders(headers);


        let requestMethodStr = 'RequestMethod.Post'.replace(/RequestMethod\./, '').toUpperCase();
        let requestPath = path;
        let authState = '';

        if (Object.keys(queryParameters).length > 0) {
            requestPath = `${requestPath}?${Object.keys(queryParameters)
                .map(key => `${encodeURIComponent(key)}=${encodeURIComponent(queryParameters[key])}`).join('&')}`;
        }

        if (this.configuration.accessToken) 
            authState = `(authenticated) `;
        
        return this.http
            .request<models.ApiUserAuthToken>(requestMethodStr, requestPath, { 
                observe: 'response', 
                responseType: 'json',
                headers, 
                withCredentials: this.configuration.withCredentials,
            })
            .pipe(
                catchError(e => {
                    let time = Date.now() - traceStart;

                    if (e instanceof HttpErrorResponse) {
                        let error = <HttpErrorResponse>e;
                        this.logger.info(`[API] ${requestMethodStr} ${authState}${requestPath} (${time} ms): Error: ${error.status} ${error.statusText}`);
                        e = error.error || e;
                    }

                    return throwError(e);
                }),
                map(response => {
                    let time = Date.now() - traceStart;
                    this.logger.info(`[API] ${requestMethodStr} ${authState}${requestPath} (${time} ms): Finished: ${response.status} ${response.statusText}`);

                    return response;
                })
            )
        ;
    }

    /**
     * Send a password reset request
     * 
     * @param body 
     */
    public requestPasswordResetRequest(body?: models.ApiPostUsersRequestpasswordreset, extraHttpRequestParams?: any): Observable<HttpResponse<{}>> {
        const path = this.basePath + '/users/requestPasswordReset';

        let traceStart = Date.now();
        let queryParameters : Record<string,string> = {};

        let globalQueryParams = (this.configuration.globalQueryParams || {});
        Object.keys(globalQueryParams).forEach (x => queryParameters[x] = globalQueryParams[x]);
        
        let headers : Record<string,string> = Object.assign({}, this.defaultHeaders); 

        this.applyGlobalHeaders(headers);

        headers['Content-Type'] = 'application/json';


        let requestMethodStr = 'RequestMethod.Post'.replace(/RequestMethod\./, '').toUpperCase();
        let requestPath = path;
        let authState = '';

        if (Object.keys(queryParameters).length > 0) {
            requestPath = `${requestPath}?${Object.keys(queryParameters)
                .map(key => `${encodeURIComponent(key)}=${encodeURIComponent(queryParameters[key])}`).join('&')}`;
        }

        if (this.configuration.accessToken) 
            authState = `(authenticated) `;
        
        return this.http
            .request<any>(requestMethodStr, requestPath, { 
                observe: 'response', 
                responseType: 'json',
                headers, 
                withCredentials: this.configuration.withCredentials,
                body: body == null ? '' : JSON.stringify(body), // https://github.com/angular/angular/issues/10612
            })
            .pipe(
                catchError(e => {
                    let time = Date.now() - traceStart;

                    if (e instanceof HttpErrorResponse) {
                        let error = <HttpErrorResponse>e;
                        this.logger.info(`[API] ${requestMethodStr} ${authState}${requestPath} (${time} ms): Error: ${error.status} ${error.statusText}`);
                        e = error.error || e;
                    }

                    return throwError(e);
                }),
                map(response => {
                    let time = Date.now() - traceStart;
                    this.logger.info(`[API] ${requestMethodStr} ${authState}${requestPath} (${time} ms): Finished: ${response.status} ${response.statusText}`);

                    return response;
                })
            )
        ;
    }

    /**
     * Authenticate using twitter, facebook, or google oauth credentials
     * 
     * @param body 
     */
    public userOmniauthAuthRequest(body?: models.ApiPostUsersOmniauth, extraHttpRequestParams?: any): Observable<HttpResponse<models.ApiUserAuthToken>> {
        const path = this.basePath + '/users/omniauth';

        let traceStart = Date.now();
        let queryParameters : Record<string,string> = {};

        let globalQueryParams = (this.configuration.globalQueryParams || {});
        Object.keys(globalQueryParams).forEach (x => queryParameters[x] = globalQueryParams[x]);
        
        let headers : Record<string,string> = Object.assign({}, this.defaultHeaders); 

        this.applyGlobalHeaders(headers);

        headers['Content-Type'] = 'application/json';


        let requestMethodStr = 'RequestMethod.Post'.replace(/RequestMethod\./, '').toUpperCase();
        let requestPath = path;
        let authState = '';

        if (Object.keys(queryParameters).length > 0) {
            requestPath = `${requestPath}?${Object.keys(queryParameters)
                .map(key => `${encodeURIComponent(key)}=${encodeURIComponent(queryParameters[key])}`).join('&')}`;
        }

        if (this.configuration.accessToken) 
            authState = `(authenticated) `;
        
        return this.http
            .request<models.ApiUserAuthToken>(requestMethodStr, requestPath, { 
                observe: 'response', 
                responseType: 'json',
                headers, 
                withCredentials: this.configuration.withCredentials,
                body: body == null ? '' : JSON.stringify(body), // https://github.com/angular/angular/issues/10612
            })
            .pipe(
                catchError(e => {
                    let time = Date.now() - traceStart;

                    if (e instanceof HttpErrorResponse) {
                        let error = <HttpErrorResponse>e;
                        this.logger.info(`[API] ${requestMethodStr} ${authState}${requestPath} (${time} ms): Error: ${error.status} ${error.statusText}`);
                        e = error.error || e;
                    }

                    return throwError(e);
                }),
                map(response => {
                    let time = Date.now() - traceStart;
                    this.logger.info(`[API] ${requestMethodStr} ${authState}${requestPath} (${time} ms): Finished: ${response.status} ${response.statusText}`);

                    return response;
                })
            )
        ;
    }

    /**
     * 
     * 
     * @param body 
     */
    public authenticateRequest(body?: models.ApiPostUsersAuth, extraHttpRequestParams?: any): Observable<HttpResponse<models.ApiUserAuthToken>> {
        const path = this.basePath + '/users/auth';

        let traceStart = Date.now();
        let queryParameters : Record<string,string> = {};

        let globalQueryParams = (this.configuration.globalQueryParams || {});
        Object.keys(globalQueryParams).forEach (x => queryParameters[x] = globalQueryParams[x]);
        
        let headers : Record<string,string> = Object.assign({}, this.defaultHeaders); 

        this.applyGlobalHeaders(headers);

        headers['Content-Type'] = 'application/json';


        let requestMethodStr = 'RequestMethod.Post'.replace(/RequestMethod\./, '').toUpperCase();
        let requestPath = path;
        let authState = '';

        if (Object.keys(queryParameters).length > 0) {
            requestPath = `${requestPath}?${Object.keys(queryParameters)
                .map(key => `${encodeURIComponent(key)}=${encodeURIComponent(queryParameters[key])}`).join('&')}`;
        }

        if (this.configuration.accessToken) 
            authState = `(authenticated) `;
        
        return this.http
            .request<models.ApiUserAuthToken>(requestMethodStr, requestPath, { 
                observe: 'response', 
                responseType: 'json',
                headers, 
                withCredentials: this.configuration.withCredentials,
                body: body == null ? '' : JSON.stringify(body), // https://github.com/angular/angular/issues/10612
            })
            .pipe(
                catchError(e => {
                    let time = Date.now() - traceStart;

                    if (e instanceof HttpErrorResponse) {
                        let error = <HttpErrorResponse>e;
                        this.logger.info(`[API] ${requestMethodStr} ${authState}${requestPath} (${time} ms): Error: ${error.status} ${error.statusText}`);
                        e = error.error || e;
                    }

                    return throwError(e);
                }),
                map(response => {
                    let time = Date.now() - traceStart;
                    this.logger.info(`[API] ${requestMethodStr} ${authState}${requestPath} (${time} ms): Finished: ${response.status} ${response.statusText}`);

                    return response;
                })
            )
        ;
    }

    /**
     * 
     * 
     */
    public postUsersSsoRequest(extraHttpRequestParams?: any): Observable<HttpResponse<{}>> {
        const path = this.basePath + '/users/sso';

        let traceStart = Date.now();
        let queryParameters : Record<string,string> = {};

        let globalQueryParams = (this.configuration.globalQueryParams || {});
        Object.keys(globalQueryParams).forEach (x => queryParameters[x] = globalQueryParams[x]);
        
        let headers : Record<string,string> = Object.assign({}, this.defaultHeaders); 

        this.applyGlobalHeaders(headers);


        let requestMethodStr = 'RequestMethod.Post'.replace(/RequestMethod\./, '').toUpperCase();
        let requestPath = path;
        let authState = '';

        if (Object.keys(queryParameters).length > 0) {
            requestPath = `${requestPath}?${Object.keys(queryParameters)
                .map(key => `${encodeURIComponent(key)}=${encodeURIComponent(queryParameters[key])}`).join('&')}`;
        }

        if (this.configuration.accessToken) 
            authState = `(authenticated) `;
        
        return this.http
            .request<any>(requestMethodStr, requestPath, { 
                observe: 'response', 
                responseType: 'json',
                headers, 
                withCredentials: this.configuration.withCredentials,
            })
            .pipe(
                catchError(e => {
                    let time = Date.now() - traceStart;

                    if (e instanceof HttpErrorResponse) {
                        let error = <HttpErrorResponse>e;
                        this.logger.info(`[API] ${requestMethodStr} ${authState}${requestPath} (${time} ms): Error: ${error.status} ${error.statusText}`);
                        e = error.error || e;
                    }

                    return throwError(e);
                }),
                map(response => {
                    let time = Date.now() - traceStart;
                    this.logger.info(`[API] ${requestMethodStr} ${authState}${requestPath} (${time} ms): Finished: ${response.status} ${response.statusText}`);

                    return response;
                })
            )
        ;
    }

    /**
     * 
     * 
     */
    public startDataDownloadRequest(extraHttpRequestParams?: any): Observable<HttpResponse<{}>> {
        const path = this.basePath + '/users/current/data-download/start';

        let traceStart = Date.now();
        let queryParameters : Record<string,string> = {};

        let globalQueryParams = (this.configuration.globalQueryParams || {});
        Object.keys(globalQueryParams).forEach (x => queryParameters[x] = globalQueryParams[x]);
        
        let headers : Record<string,string> = Object.assign({}, this.defaultHeaders); 

        this.applyGlobalHeaders(headers);


        let requestMethodStr = 'RequestMethod.Post'.replace(/RequestMethod\./, '').toUpperCase();
        let requestPath = path;
        let authState = '';

        if (Object.keys(queryParameters).length > 0) {
            requestPath = `${requestPath}?${Object.keys(queryParameters)
                .map(key => `${encodeURIComponent(key)}=${encodeURIComponent(queryParameters[key])}`).join('&')}`;
        }

        if (this.configuration.accessToken) 
            authState = `(authenticated) `;
        
        return this.http
            .request<any>(requestMethodStr, requestPath, { 
                observe: 'response', 
                responseType: 'json',
                headers, 
                withCredentials: this.configuration.withCredentials,
            })
            .pipe(
                catchError(e => {
                    let time = Date.now() - traceStart;

                    if (e instanceof HttpErrorResponse) {
                        let error = <HttpErrorResponse>e;
                        this.logger.info(`[API] ${requestMethodStr} ${authState}${requestPath} (${time} ms): Error: ${error.status} ${error.statusText}`);
                        e = error.error || e;
                    }

                    return throwError(e);
                }),
                map(response => {
                    let time = Date.now() - traceStart;
                    this.logger.info(`[API] ${requestMethodStr} ${authState}${requestPath} (${time} ms): Finished: ${response.status} ${response.statusText}`);

                    return response;
                })
            )
        ;
    }

    /**
     * 
     * 
     */
    public getPaymentSourceRequest(extraHttpRequestParams?: any): Observable<HttpResponse<models.ApiPaymentSource>> {
        const path = this.basePath + '/users/current/payment';

        let traceStart = Date.now();
        let queryParameters : Record<string,string> = {};

        let globalQueryParams = (this.configuration.globalQueryParams || {});
        Object.keys(globalQueryParams).forEach (x => queryParameters[x] = globalQueryParams[x]);
        
        let headers : Record<string,string> = Object.assign({}, this.defaultHeaders); 

        this.applyGlobalHeaders(headers);


        let requestMethodStr = 'RequestMethod.Get'.replace(/RequestMethod\./, '').toUpperCase();
        let requestPath = path;
        let authState = '';

        if (Object.keys(queryParameters).length > 0) {
            requestPath = `${requestPath}?${Object.keys(queryParameters)
                .map(key => `${encodeURIComponent(key)}=${encodeURIComponent(queryParameters[key])}`).join('&')}`;
        }

        if (this.configuration.accessToken) 
            authState = `(authenticated) `;
        
        return this.http
            .request<models.ApiPaymentSource>(requestMethodStr, requestPath, { 
                observe: 'response', 
                responseType: 'json',
                headers, 
                withCredentials: this.configuration.withCredentials,
            })
            .pipe(
                catchError(e => {
                    let time = Date.now() - traceStart;

                    if (e instanceof HttpErrorResponse) {
                        let error = <HttpErrorResponse>e;
                        this.logger.info(`[API] ${requestMethodStr} ${authState}${requestPath} (${time} ms): Error: ${error.status} ${error.statusText}`);
                        e = error.error || e;
                    }

                    return throwError(e);
                }),
                map(response => {
                    let time = Date.now() - traceStart;
                    this.logger.info(`[API] ${requestMethodStr} ${authState}${requestPath} (${time} ms): Finished: ${response.status} ${response.statusText}`);

                    return response;
                })
            )
        ;
    }

    /**
     * 
     * 
     * @param body 
     */
    public checkPermissionRequest(body?: models.ApiPostUsersCurrentPermissionsCheck, extraHttpRequestParams?: any): Observable<HttpResponse<models.ApiPermissionCheckResult>> {
        const path = this.basePath + '/users/current/permissions/check';

        let traceStart = Date.now();
        let queryParameters : Record<string,string> = {};

        let globalQueryParams = (this.configuration.globalQueryParams || {});
        Object.keys(globalQueryParams).forEach (x => queryParameters[x] = globalQueryParams[x]);
        
        let headers : Record<string,string> = Object.assign({}, this.defaultHeaders); 

        this.applyGlobalHeaders(headers);

        headers['Content-Type'] = 'application/json';


        let requestMethodStr = 'RequestMethod.Post'.replace(/RequestMethod\./, '').toUpperCase();
        let requestPath = path;
        let authState = '';

        if (Object.keys(queryParameters).length > 0) {
            requestPath = `${requestPath}?${Object.keys(queryParameters)
                .map(key => `${encodeURIComponent(key)}=${encodeURIComponent(queryParameters[key])}`).join('&')}`;
        }

        if (this.configuration.accessToken) 
            authState = `(authenticated) `;
        
        return this.http
            .request<models.ApiPermissionCheckResult>(requestMethodStr, requestPath, { 
                observe: 'response', 
                responseType: 'json',
                headers, 
                withCredentials: this.configuration.withCredentials,
                body: body == null ? '' : JSON.stringify(body), // https://github.com/angular/angular/issues/10612
            })
            .pipe(
                catchError(e => {
                    let time = Date.now() - traceStart;

                    if (e instanceof HttpErrorResponse) {
                        let error = <HttpErrorResponse>e;
                        this.logger.info(`[API] ${requestMethodStr} ${authState}${requestPath} (${time} ms): Error: ${error.status} ${error.statusText}`);
                        e = error.error || e;
                    }

                    return throwError(e);
                }),
                map(response => {
                    let time = Date.now() - traceStart;
                    this.logger.info(`[API] ${requestMethodStr} ${authState}${requestPath} (${time} ms): Finished: ${response.status} ${response.statusText}`);

                    return response;
                })
            )
        ;
    }

    /**
     * 
     * 
     * @param page 
     */
    public getAccountTransactionsRequest(page?: number, extraHttpRequestParams?: any): Observable<HttpResponse<PagedArray<models.ApiAccountTransaction>>> {
        const path = this.basePath + '/users/current/transactions';

        let traceStart = Date.now();
        let queryParameters : Record<string,string> = {};

        let globalQueryParams = (this.configuration.globalQueryParams || {});
        Object.keys(globalQueryParams).forEach (x => queryParameters[x] = globalQueryParams[x]);
        
        let headers : Record<string,string> = Object.assign({}, this.defaultHeaders); 
        if (page !== undefined) {
            queryParameters['page'] = <any>page;
        }


        this.applyGlobalHeaders(headers);


        let requestMethodStr = 'RequestMethod.Get'.replace(/RequestMethod\./, '').toUpperCase();
        let requestPath = path;
        let authState = '';

        if (Object.keys(queryParameters).length > 0) {
            requestPath = `${requestPath}?${Object.keys(queryParameters)
                .map(key => `${encodeURIComponent(key)}=${encodeURIComponent(queryParameters[key])}`).join('&')}`;
        }

        if (this.configuration.accessToken) 
            authState = `(authenticated) `;
        
        return this.http
            .request<PagedArray<models.ApiAccountTransaction>>(requestMethodStr, requestPath, { 
                observe: 'response', 
                responseType: 'json',
                headers, 
                withCredentials: this.configuration.withCredentials,
            })
            .pipe(
                catchError(e => {
                    let time = Date.now() - traceStart;

                    if (e instanceof HttpErrorResponse) {
                        let error = <HttpErrorResponse>e;
                        this.logger.info(`[API] ${requestMethodStr} ${authState}${requestPath} (${time} ms): Error: ${error.status} ${error.statusText}`);
                        e = error.error || e;
                    }

                    return throwError(e);
                }),
                map(response => {
                    let time = Date.now() - traceStart;
                    this.logger.info(`[API] ${requestMethodStr} ${authState}${requestPath} (${time} ms): Finished: ${response.status} ${response.statusText}`);

                    return response;
                })
            )
        ;
    }

    /**
     * 
     * 
     * @param body 
     */
    public deleteCurrentUserRequest(body?: models.ApiPostUsersCurrentDelete, extraHttpRequestParams?: any): Observable<HttpResponse<models.ApiDeletionConfirmation>> {
        const path = this.basePath + '/users/current/delete';

        let traceStart = Date.now();
        let queryParameters : Record<string,string> = {};

        let globalQueryParams = (this.configuration.globalQueryParams || {});
        Object.keys(globalQueryParams).forEach (x => queryParameters[x] = globalQueryParams[x]);
        
        let headers : Record<string,string> = Object.assign({}, this.defaultHeaders); 

        this.applyGlobalHeaders(headers);

        headers['Content-Type'] = 'application/json';


        let requestMethodStr = 'RequestMethod.Post'.replace(/RequestMethod\./, '').toUpperCase();
        let requestPath = path;
        let authState = '';

        if (Object.keys(queryParameters).length > 0) {
            requestPath = `${requestPath}?${Object.keys(queryParameters)
                .map(key => `${encodeURIComponent(key)}=${encodeURIComponent(queryParameters[key])}`).join('&')}`;
        }

        if (this.configuration.accessToken) 
            authState = `(authenticated) `;
        
        return this.http
            .request<models.ApiDeletionConfirmation>(requestMethodStr, requestPath, { 
                observe: 'response', 
                responseType: 'json',
                headers, 
                withCredentials: this.configuration.withCredentials,
                body: body == null ? '' : JSON.stringify(body), // https://github.com/angular/angular/issues/10612
            })
            .pipe(
                catchError(e => {
                    let time = Date.now() - traceStart;

                    if (e instanceof HttpErrorResponse) {
                        let error = <HttpErrorResponse>e;
                        this.logger.info(`[API] ${requestMethodStr} ${authState}${requestPath} (${time} ms): Error: ${error.status} ${error.statusText}`);
                        e = error.error || e;
                    }

                    return throwError(e);
                }),
                map(response => {
                    let time = Date.now() - traceStart;
                    this.logger.info(`[API] ${requestMethodStr} ${authState}${requestPath} (${time} ms): Finished: ${response.status} ${response.statusText}`);

                    return response;
                })
            )
        ;
    }

    /**
     * 
     * 
     */
    public getCurrentUserRequest(extraHttpRequestParams?: any): Observable<HttpResponse<models.ApiUser>> {
        const path = this.basePath + '/users/current';

        let traceStart = Date.now();
        let queryParameters : Record<string,string> = {};

        let globalQueryParams = (this.configuration.globalQueryParams || {});
        Object.keys(globalQueryParams).forEach (x => queryParameters[x] = globalQueryParams[x]);
        
        let headers : Record<string,string> = Object.assign({}, this.defaultHeaders); 

        this.applyGlobalHeaders(headers);


        let requestMethodStr = 'RequestMethod.Get'.replace(/RequestMethod\./, '').toUpperCase();
        let requestPath = path;
        let authState = '';

        if (Object.keys(queryParameters).length > 0) {
            requestPath = `${requestPath}?${Object.keys(queryParameters)
                .map(key => `${encodeURIComponent(key)}=${encodeURIComponent(queryParameters[key])}`).join('&')}`;
        }

        if (this.configuration.accessToken) 
            authState = `(authenticated) `;
        
        return this.http
            .request<models.ApiUser>(requestMethodStr, requestPath, { 
                observe: 'response', 
                responseType: 'json',
                headers, 
                withCredentials: this.configuration.withCredentials,
            })
            .pipe(
                catchError(e => {
                    let time = Date.now() - traceStart;

                    if (e instanceof HttpErrorResponse) {
                        let error = <HttpErrorResponse>e;
                        this.logger.info(`[API] ${requestMethodStr} ${authState}${requestPath} (${time} ms): Error: ${error.status} ${error.statusText}`);
                        e = error.error || e;
                    }

                    return throwError(e);
                }),
                map(response => {
                    let time = Date.now() - traceStart;
                    this.logger.info(`[API] ${requestMethodStr} ${authState}${requestPath} (${time} ms): Finished: ${response.status} ${response.statusText}`);

                    return response;
                })
            )
        ;
    }

    /**
     * 
     * 
     * @param body 
     */
    public updateUserRequest(body?: models.ApiPostUsersCurrent, extraHttpRequestParams?: any): Observable<HttpResponse<models.ApiUser>> {
        const path = this.basePath + '/users/current';

        let traceStart = Date.now();
        let queryParameters : Record<string,string> = {};

        let globalQueryParams = (this.configuration.globalQueryParams || {});
        Object.keys(globalQueryParams).forEach (x => queryParameters[x] = globalQueryParams[x]);
        
        let headers : Record<string,string> = Object.assign({}, this.defaultHeaders); 

        this.applyGlobalHeaders(headers);

        headers['Content-Type'] = 'application/json';


        let requestMethodStr = 'RequestMethod.Post'.replace(/RequestMethod\./, '').toUpperCase();
        let requestPath = path;
        let authState = '';

        if (Object.keys(queryParameters).length > 0) {
            requestPath = `${requestPath}?${Object.keys(queryParameters)
                .map(key => `${encodeURIComponent(key)}=${encodeURIComponent(queryParameters[key])}`).join('&')}`;
        }

        if (this.configuration.accessToken) 
            authState = `(authenticated) `;
        
        return this.http
            .request<models.ApiUser>(requestMethodStr, requestPath, { 
                observe: 'response', 
                responseType: 'json',
                headers, 
                withCredentials: this.configuration.withCredentials,
                body: body == null ? '' : JSON.stringify(body), // https://github.com/angular/angular/issues/10612
            })
            .pipe(
                catchError(e => {
                    let time = Date.now() - traceStart;

                    if (e instanceof HttpErrorResponse) {
                        let error = <HttpErrorResponse>e;
                        this.logger.info(`[API] ${requestMethodStr} ${authState}${requestPath} (${time} ms): Error: ${error.status} ${error.statusText}`);
                        e = error.error || e;
                    }

                    return throwError(e);
                }),
                map(response => {
                    let time = Date.now() - traceStart;
                    this.logger.info(`[API] ${requestMethodStr} ${authState}${requestPath} (${time} ms): Finished: ${response.status} ${response.statusText}`);

                    return response;
                })
            )
        ;
    }

    /**
     * 
     * 
     * @param offer_set_uuid 
     * @param offer_uuid 
     */
    public getStripePaymentIntentRequest(offer_set_uuid?: string, offer_uuid?: string, extraHttpRequestParams?: any): Observable<HttpResponse<models.ApiStripePaymentIntent>> {
        const path = this.basePath + '/users/current/stripe/payment-intent/offer-sets/{offer_set_uuid}/{offer_uuid}'
                    .replace('{' + 'offer_set_uuid' + '}', String(offer_set_uuid))
                    .replace('{' + 'offer_uuid' + '}', String(offer_uuid));

        let traceStart = Date.now();
        let queryParameters : Record<string,string> = {};

        let globalQueryParams = (this.configuration.globalQueryParams || {});
        Object.keys(globalQueryParams).forEach (x => queryParameters[x] = globalQueryParams[x]);
        
        let headers : Record<string,string> = Object.assign({}, this.defaultHeaders); 

        this.applyGlobalHeaders(headers);


        let requestMethodStr = 'RequestMethod.Get'.replace(/RequestMethod\./, '').toUpperCase();
        let requestPath = path;
        let authState = '';

        if (Object.keys(queryParameters).length > 0) {
            requestPath = `${requestPath}?${Object.keys(queryParameters)
                .map(key => `${encodeURIComponent(key)}=${encodeURIComponent(queryParameters[key])}`).join('&')}`;
        }

        if (this.configuration.accessToken) 
            authState = `(authenticated) `;
        
        return this.http
            .request<models.ApiStripePaymentIntent>(requestMethodStr, requestPath, { 
                observe: 'response', 
                responseType: 'json',
                headers, 
                withCredentials: this.configuration.withCredentials,
            })
            .pipe(
                catchError(e => {
                    let time = Date.now() - traceStart;

                    if (e instanceof HttpErrorResponse) {
                        let error = <HttpErrorResponse>e;
                        this.logger.info(`[API] ${requestMethodStr} ${authState}${requestPath} (${time} ms): Error: ${error.status} ${error.statusText}`);
                        e = error.error || e;
                    }

                    return throwError(e);
                }),
                map(response => {
                    let time = Date.now() - traceStart;
                    this.logger.info(`[API] ${requestMethodStr} ${authState}${requestPath} (${time} ms): Finished: ${response.status} ${response.statusText}`);

                    return response;
                })
            )
        ;
    }

    /**
     * 
     * 
     * @param code 
     */
    public getRetentionOfferRequest(code?: string, extraHttpRequestParams?: any): Observable<HttpResponse<models.ApiRetentionOfferSet>> {
        const path = this.basePath + '/users/membership/retention/offers/{code}'
                    .replace('{' + 'code' + '}', String(code));

        let traceStart = Date.now();
        let queryParameters : Record<string,string> = {};

        let globalQueryParams = (this.configuration.globalQueryParams || {});
        Object.keys(globalQueryParams).forEach (x => queryParameters[x] = globalQueryParams[x]);
        
        let headers : Record<string,string> = Object.assign({}, this.defaultHeaders); 

        this.applyGlobalHeaders(headers);


        let requestMethodStr = 'RequestMethod.Post'.replace(/RequestMethod\./, '').toUpperCase();
        let requestPath = path;
        let authState = '';

        if (Object.keys(queryParameters).length > 0) {
            requestPath = `${requestPath}?${Object.keys(queryParameters)
                .map(key => `${encodeURIComponent(key)}=${encodeURIComponent(queryParameters[key])}`).join('&')}`;
        }

        if (this.configuration.accessToken) 
            authState = `(authenticated) `;
        
        return this.http
            .request<models.ApiRetentionOfferSet>(requestMethodStr, requestPath, { 
                observe: 'response', 
                responseType: 'json',
                headers, 
                withCredentials: this.configuration.withCredentials,
            })
            .pipe(
                catchError(e => {
                    let time = Date.now() - traceStart;

                    if (e instanceof HttpErrorResponse) {
                        let error = <HttpErrorResponse>e;
                        this.logger.info(`[API] ${requestMethodStr} ${authState}${requestPath} (${time} ms): Error: ${error.status} ${error.statusText}`);
                        e = error.error || e;
                    }

                    return throwError(e);
                }),
                map(response => {
                    let time = Date.now() - traceStart;
                    this.logger.info(`[API] ${requestMethodStr} ${authState}${requestPath} (${time} ms): Finished: ${response.status} ${response.statusText}`);

                    return response;
                })
            )
        ;
    }

    /**
     * 
     * 
     */
    public reactivateMembershipRequest(extraHttpRequestParams?: any): Observable<HttpResponse<models.ApiMembership>> {
        const path = this.basePath + '/users/current/membership/reactivate';

        let traceStart = Date.now();
        let queryParameters : Record<string,string> = {};

        let globalQueryParams = (this.configuration.globalQueryParams || {});
        Object.keys(globalQueryParams).forEach (x => queryParameters[x] = globalQueryParams[x]);
        
        let headers : Record<string,string> = Object.assign({}, this.defaultHeaders); 

        this.applyGlobalHeaders(headers);


        let requestMethodStr = 'RequestMethod.Post'.replace(/RequestMethod\./, '').toUpperCase();
        let requestPath = path;
        let authState = '';

        if (Object.keys(queryParameters).length > 0) {
            requestPath = `${requestPath}?${Object.keys(queryParameters)
                .map(key => `${encodeURIComponent(key)}=${encodeURIComponent(queryParameters[key])}`).join('&')}`;
        }

        if (this.configuration.accessToken) 
            authState = `(authenticated) `;
        
        return this.http
            .request<models.ApiMembership>(requestMethodStr, requestPath, { 
                observe: 'response', 
                responseType: 'json',
                headers, 
                withCredentials: this.configuration.withCredentials,
            })
            .pipe(
                catchError(e => {
                    let time = Date.now() - traceStart;

                    if (e instanceof HttpErrorResponse) {
                        let error = <HttpErrorResponse>e;
                        this.logger.info(`[API] ${requestMethodStr} ${authState}${requestPath} (${time} ms): Error: ${error.status} ${error.statusText}`);
                        e = error.error || e;
                    }

                    return throwError(e);
                }),
                map(response => {
                    let time = Date.now() - traceStart;
                    this.logger.info(`[API] ${requestMethodStr} ${authState}${requestPath} (${time} ms): Finished: ${response.status} ${response.statusText}`);

                    return response;
                })
            )
        ;
    }

    /**
     * 
     * 
     * @param body 
     */
    public cancelMembershipRequest(body?: models.ApiPostUsersCurrentMembershipCancel, extraHttpRequestParams?: any): Observable<HttpResponse<models.ApiMembership>> {
        const path = this.basePath + '/users/current/membership/cancel';

        let traceStart = Date.now();
        let queryParameters : Record<string,string> = {};

        let globalQueryParams = (this.configuration.globalQueryParams || {});
        Object.keys(globalQueryParams).forEach (x => queryParameters[x] = globalQueryParams[x]);
        
        let headers : Record<string,string> = Object.assign({}, this.defaultHeaders); 

        this.applyGlobalHeaders(headers);

        headers['Content-Type'] = 'application/json';


        let requestMethodStr = 'RequestMethod.Post'.replace(/RequestMethod\./, '').toUpperCase();
        let requestPath = path;
        let authState = '';

        if (Object.keys(queryParameters).length > 0) {
            requestPath = `${requestPath}?${Object.keys(queryParameters)
                .map(key => `${encodeURIComponent(key)}=${encodeURIComponent(queryParameters[key])}`).join('&')}`;
        }

        if (this.configuration.accessToken) 
            authState = `(authenticated) `;
        
        return this.http
            .request<models.ApiMembership>(requestMethodStr, requestPath, { 
                observe: 'response', 
                responseType: 'json',
                headers, 
                withCredentials: this.configuration.withCredentials,
                body: body == null ? '' : JSON.stringify(body), // https://github.com/angular/angular/issues/10612
            })
            .pipe(
                catchError(e => {
                    let time = Date.now() - traceStart;

                    if (e instanceof HttpErrorResponse) {
                        let error = <HttpErrorResponse>e;
                        this.logger.info(`[API] ${requestMethodStr} ${authState}${requestPath} (${time} ms): Error: ${error.status} ${error.statusText}`);
                        e = error.error || e;
                    }

                    return throwError(e);
                }),
                map(response => {
                    let time = Date.now() - traceStart;
                    this.logger.info(`[API] ${requestMethodStr} ${authState}${requestPath} (${time} ms): Finished: ${response.status} ${response.statusText}`);

                    return response;
                })
            )
        ;
    }

    /**
     * 
     * 
     * @param body 
     */
    public previewMembershipChangeRequest(body?: models.ApiPostUsersCurrentMembershipModifyPreview, extraHttpRequestParams?: any): Observable<HttpResponse<models.ApiMembershipChangePreview>> {
        const path = this.basePath + '/users/current/membership/modify/preview';

        let traceStart = Date.now();
        let queryParameters : Record<string,string> = {};

        let globalQueryParams = (this.configuration.globalQueryParams || {});
        Object.keys(globalQueryParams).forEach (x => queryParameters[x] = globalQueryParams[x]);
        
        let headers : Record<string,string> = Object.assign({}, this.defaultHeaders); 

        this.applyGlobalHeaders(headers);

        headers['Content-Type'] = 'application/json';


        let requestMethodStr = 'RequestMethod.Post'.replace(/RequestMethod\./, '').toUpperCase();
        let requestPath = path;
        let authState = '';

        if (Object.keys(queryParameters).length > 0) {
            requestPath = `${requestPath}?${Object.keys(queryParameters)
                .map(key => `${encodeURIComponent(key)}=${encodeURIComponent(queryParameters[key])}`).join('&')}`;
        }

        if (this.configuration.accessToken) 
            authState = `(authenticated) `;
        
        return this.http
            .request<models.ApiMembershipChangePreview>(requestMethodStr, requestPath, { 
                observe: 'response', 
                responseType: 'json',
                headers, 
                withCredentials: this.configuration.withCredentials,
                body: body == null ? '' : JSON.stringify(body), // https://github.com/angular/angular/issues/10612
            })
            .pipe(
                catchError(e => {
                    let time = Date.now() - traceStart;

                    if (e instanceof HttpErrorResponse) {
                        let error = <HttpErrorResponse>e;
                        this.logger.info(`[API] ${requestMethodStr} ${authState}${requestPath} (${time} ms): Error: ${error.status} ${error.statusText}`);
                        e = error.error || e;
                    }

                    return throwError(e);
                }),
                map(response => {
                    let time = Date.now() - traceStart;
                    this.logger.info(`[API] ${requestMethodStr} ${authState}${requestPath} (${time} ms): Finished: ${response.status} ${response.statusText}`);

                    return response;
                })
            )
        ;
    }

    /**
     * 
     * 
     * @param body 
     */
    public modifyMembershipRequest(body?: models.ApiPostUsersCurrentMembershipModify, extraHttpRequestParams?: any): Observable<HttpResponse<models.ApiMembership>> {
        const path = this.basePath + '/users/current/membership/modify';

        let traceStart = Date.now();
        let queryParameters : Record<string,string> = {};

        let globalQueryParams = (this.configuration.globalQueryParams || {});
        Object.keys(globalQueryParams).forEach (x => queryParameters[x] = globalQueryParams[x]);
        
        let headers : Record<string,string> = Object.assign({}, this.defaultHeaders); 

        this.applyGlobalHeaders(headers);

        headers['Content-Type'] = 'application/json';


        let requestMethodStr = 'RequestMethod.Post'.replace(/RequestMethod\./, '').toUpperCase();
        let requestPath = path;
        let authState = '';

        if (Object.keys(queryParameters).length > 0) {
            requestPath = `${requestPath}?${Object.keys(queryParameters)
                .map(key => `${encodeURIComponent(key)}=${encodeURIComponent(queryParameters[key])}`).join('&')}`;
        }

        if (this.configuration.accessToken) 
            authState = `(authenticated) `;
        
        return this.http
            .request<models.ApiMembership>(requestMethodStr, requestPath, { 
                observe: 'response', 
                responseType: 'json',
                headers, 
                withCredentials: this.configuration.withCredentials,
                body: body == null ? '' : JSON.stringify(body), // https://github.com/angular/angular/issues/10612
            })
            .pipe(
                catchError(e => {
                    let time = Date.now() - traceStart;

                    if (e instanceof HttpErrorResponse) {
                        let error = <HttpErrorResponse>e;
                        this.logger.info(`[API] ${requestMethodStr} ${authState}${requestPath} (${time} ms): Error: ${error.status} ${error.statusText}`);
                        e = error.error || e;
                    }

                    return throwError(e);
                }),
                map(response => {
                    let time = Date.now() - traceStart;
                    this.logger.info(`[API] ${requestMethodStr} ${authState}${requestPath} (${time} ms): Finished: ${response.status} ${response.statusText}`);

                    return response;
                })
            )
        ;
    }

    /**
     * 
     * 
     * @param body 
     */
    public refreshAuthenticationRequest(body?: models.ApiPostUsersAuthRefresh, extraHttpRequestParams?: any): Observable<HttpResponse<models.ApiUserAuthToken>> {
        const path = this.basePath + '/users/auth/refresh';

        let traceStart = Date.now();
        let queryParameters : Record<string,string> = {};

        let globalQueryParams = (this.configuration.globalQueryParams || {});
        Object.keys(globalQueryParams).forEach (x => queryParameters[x] = globalQueryParams[x]);
        
        let headers : Record<string,string> = Object.assign({}, this.defaultHeaders); 

        this.applyGlobalHeaders(headers);

        headers['Content-Type'] = 'application/json';


        let requestMethodStr = 'RequestMethod.Post'.replace(/RequestMethod\./, '').toUpperCase();
        let requestPath = path;
        let authState = '';

        if (Object.keys(queryParameters).length > 0) {
            requestPath = `${requestPath}?${Object.keys(queryParameters)
                .map(key => `${encodeURIComponent(key)}=${encodeURIComponent(queryParameters[key])}`).join('&')}`;
        }

        if (this.configuration.accessToken) 
            authState = `(authenticated) `;
        
        return this.http
            .request<models.ApiUserAuthToken>(requestMethodStr, requestPath, { 
                observe: 'response', 
                responseType: 'json',
                headers, 
                withCredentials: this.configuration.withCredentials,
                body: body == null ? '' : JSON.stringify(body), // https://github.com/angular/angular/issues/10612
            })
            .pipe(
                catchError(e => {
                    let time = Date.now() - traceStart;

                    if (e instanceof HttpErrorResponse) {
                        let error = <HttpErrorResponse>e;
                        this.logger.info(`[API] ${requestMethodStr} ${authState}${requestPath} (${time} ms): Error: ${error.status} ${error.statusText}`);
                        e = error.error || e;
                    }

                    return throwError(e);
                }),
                map(response => {
                    let time = Date.now() - traceStart;
                    this.logger.info(`[API] ${requestMethodStr} ${authState}${requestPath} (${time} ms): Finished: ${response.status} ${response.statusText}`);

                    return response;
                })
            )
        ;
    }

    /**
     * 
     * 
     * @param body 
     */
    public changePaymentSourceRequest(body?: models.ApiPostUsersCurrentMembershipChangepayment, extraHttpRequestParams?: any): Observable<HttpResponse<models.ApiMembership>> {
        const path = this.basePath + '/users/current/membership/changePayment';

        let traceStart = Date.now();
        let queryParameters : Record<string,string> = {};

        let globalQueryParams = (this.configuration.globalQueryParams || {});
        Object.keys(globalQueryParams).forEach (x => queryParameters[x] = globalQueryParams[x]);
        
        let headers : Record<string,string> = Object.assign({}, this.defaultHeaders); 

        this.applyGlobalHeaders(headers);

        headers['Content-Type'] = 'application/json';


        let requestMethodStr = 'RequestMethod.Post'.replace(/RequestMethod\./, '').toUpperCase();
        let requestPath = path;
        let authState = '';

        if (Object.keys(queryParameters).length > 0) {
            requestPath = `${requestPath}?${Object.keys(queryParameters)
                .map(key => `${encodeURIComponent(key)}=${encodeURIComponent(queryParameters[key])}`).join('&')}`;
        }

        if (this.configuration.accessToken) 
            authState = `(authenticated) `;
        
        return this.http
            .request<models.ApiMembership>(requestMethodStr, requestPath, { 
                observe: 'response', 
                responseType: 'json',
                headers, 
                withCredentials: this.configuration.withCredentials,
                body: body == null ? '' : JSON.stringify(body), // https://github.com/angular/angular/issues/10612
            })
            .pipe(
                catchError(e => {
                    let time = Date.now() - traceStart;

                    if (e instanceof HttpErrorResponse) {
                        let error = <HttpErrorResponse>e;
                        this.logger.info(`[API] ${requestMethodStr} ${authState}${requestPath} (${time} ms): Error: ${error.status} ${error.statusText}`);
                        e = error.error || e;
                    }

                    return throwError(e);
                }),
                map(response => {
                    let time = Date.now() - traceStart;
                    this.logger.info(`[API] ${requestMethodStr} ${authState}${requestPath} (${time} ms): Finished: ${response.status} ${response.statusText}`);

                    return response;
                })
            )
        ;
    }

    /**
     * 
     * 
     * @param body 
     */
    public createMembershipRequest(body?: models.ApiPostUsersCurrentMembership, extraHttpRequestParams?: any): Observable<HttpResponse<models.ApiMembership>> {
        const path = this.basePath + '/users/current/membership';

        let traceStart = Date.now();
        let queryParameters : Record<string,string> = {};

        let globalQueryParams = (this.configuration.globalQueryParams || {});
        Object.keys(globalQueryParams).forEach (x => queryParameters[x] = globalQueryParams[x]);
        
        let headers : Record<string,string> = Object.assign({}, this.defaultHeaders); 

        this.applyGlobalHeaders(headers);

        headers['Content-Type'] = 'application/json';


        let requestMethodStr = 'RequestMethod.Post'.replace(/RequestMethod\./, '').toUpperCase();
        let requestPath = path;
        let authState = '';

        if (Object.keys(queryParameters).length > 0) {
            requestPath = `${requestPath}?${Object.keys(queryParameters)
                .map(key => `${encodeURIComponent(key)}=${encodeURIComponent(queryParameters[key])}`).join('&')}`;
        }

        if (this.configuration.accessToken) 
            authState = `(authenticated) `;
        
        return this.http
            .request<models.ApiMembership>(requestMethodStr, requestPath, { 
                observe: 'response', 
                responseType: 'json',
                headers, 
                withCredentials: this.configuration.withCredentials,
                body: body == null ? '' : JSON.stringify(body), // https://github.com/angular/angular/issues/10612
            })
            .pipe(
                catchError(e => {
                    let time = Date.now() - traceStart;

                    if (e instanceof HttpErrorResponse) {
                        let error = <HttpErrorResponse>e;
                        this.logger.info(`[API] ${requestMethodStr} ${authState}${requestPath} (${time} ms): Error: ${error.status} ${error.statusText}`);
                        e = error.error || e;
                    }

                    return throwError(e);
                }),
                map(response => {
                    let time = Date.now() - traceStart;
                    this.logger.info(`[API] ${requestMethodStr} ${authState}${requestPath} (${time} ms): Finished: ${response.status} ${response.statusText}`);

                    return response;
                })
            )
        ;
    }

    /**
     * 
     * 
     * @param body 
     */
    public updateProfileRequest(body?: models.ApiPostUsersCurrentProfile, extraHttpRequestParams?: any): Observable<HttpResponse<models.ApiUserProfile>> {
        const path = this.basePath + '/users/current/profile';

        let traceStart = Date.now();
        let queryParameters : Record<string,string> = {};

        let globalQueryParams = (this.configuration.globalQueryParams || {});
        Object.keys(globalQueryParams).forEach (x => queryParameters[x] = globalQueryParams[x]);
        
        let headers : Record<string,string> = Object.assign({}, this.defaultHeaders); 

        this.applyGlobalHeaders(headers);

        headers['Content-Type'] = 'application/json';


        let requestMethodStr = 'RequestMethod.Post'.replace(/RequestMethod\./, '').toUpperCase();
        let requestPath = path;
        let authState = '';

        if (Object.keys(queryParameters).length > 0) {
            requestPath = `${requestPath}?${Object.keys(queryParameters)
                .map(key => `${encodeURIComponent(key)}=${encodeURIComponent(queryParameters[key])}`).join('&')}`;
        }

        if (this.configuration.accessToken) 
            authState = `(authenticated) `;
        
        return this.http
            .request<models.ApiUserProfile>(requestMethodStr, requestPath, { 
                observe: 'response', 
                responseType: 'json',
                headers, 
                withCredentials: this.configuration.withCredentials,
                body: body == null ? '' : JSON.stringify(body), // https://github.com/angular/angular/issues/10612
            })
            .pipe(
                catchError(e => {
                    let time = Date.now() - traceStart;

                    if (e instanceof HttpErrorResponse) {
                        let error = <HttpErrorResponse>e;
                        this.logger.info(`[API] ${requestMethodStr} ${authState}${requestPath} (${time} ms): Error: ${error.status} ${error.statusText}`);
                        e = error.error || e;
                    }

                    return throwError(e);
                }),
                map(response => {
                    let time = Date.now() - traceStart;
                    this.logger.info(`[API] ${requestMethodStr} ${authState}${requestPath} (${time} ms): Finished: ${response.status} ${response.statusText}`);

                    return response;
                })
            )
        ;
    }

    /**
     * 
     * 
     * @param body 
     */
    public updateCoverPhotoRequest(body?: models.ApiPostUsersCurrentCoverphoto, extraHttpRequestParams?: any): Observable<HttpResponse<models.ApiUserProfile>> {
        const path = this.basePath + '/users/current/coverPhoto';

        let traceStart = Date.now();
        let queryParameters : Record<string,string> = {};

        let globalQueryParams = (this.configuration.globalQueryParams || {});
        Object.keys(globalQueryParams).forEach (x => queryParameters[x] = globalQueryParams[x]);
        
        let headers : Record<string,string> = Object.assign({}, this.defaultHeaders); 

        this.applyGlobalHeaders(headers);

        headers['Content-Type'] = 'application/json';


        let requestMethodStr = 'RequestMethod.Post'.replace(/RequestMethod\./, '').toUpperCase();
        let requestPath = path;
        let authState = '';

        if (Object.keys(queryParameters).length > 0) {
            requestPath = `${requestPath}?${Object.keys(queryParameters)
                .map(key => `${encodeURIComponent(key)}=${encodeURIComponent(queryParameters[key])}`).join('&')}`;
        }

        if (this.configuration.accessToken) 
            authState = `(authenticated) `;
        
        return this.http
            .request<models.ApiUserProfile>(requestMethodStr, requestPath, { 
                observe: 'response', 
                responseType: 'json',
                headers, 
                withCredentials: this.configuration.withCredentials,
                body: body == null ? '' : JSON.stringify(body), // https://github.com/angular/angular/issues/10612
            })
            .pipe(
                catchError(e => {
                    let time = Date.now() - traceStart;

                    if (e instanceof HttpErrorResponse) {
                        let error = <HttpErrorResponse>e;
                        this.logger.info(`[API] ${requestMethodStr} ${authState}${requestPath} (${time} ms): Error: ${error.status} ${error.statusText}`);
                        e = error.error || e;
                    }

                    return throwError(e);
                }),
                map(response => {
                    let time = Date.now() - traceStart;
                    this.logger.info(`[API] ${requestMethodStr} ${authState}${requestPath} (${time} ms): Finished: ${response.status} ${response.statusText}`);

                    return response;
                })
            )
        ;
    }

    /**
     * 
     * 
     * @param body 
     */
    public updateAvatarRequest(body?: models.ApiPostUsersCurrentAvatar, extraHttpRequestParams?: any): Observable<HttpResponse<models.ApiUserProfile>> {
        const path = this.basePath + '/users/current/avatar';

        let traceStart = Date.now();
        let queryParameters : Record<string,string> = {};

        let globalQueryParams = (this.configuration.globalQueryParams || {});
        Object.keys(globalQueryParams).forEach (x => queryParameters[x] = globalQueryParams[x]);
        
        let headers : Record<string,string> = Object.assign({}, this.defaultHeaders); 

        this.applyGlobalHeaders(headers);

        headers['Content-Type'] = 'application/json';


        let requestMethodStr = 'RequestMethod.Post'.replace(/RequestMethod\./, '').toUpperCase();
        let requestPath = path;
        let authState = '';

        if (Object.keys(queryParameters).length > 0) {
            requestPath = `${requestPath}?${Object.keys(queryParameters)
                .map(key => `${encodeURIComponent(key)}=${encodeURIComponent(queryParameters[key])}`).join('&')}`;
        }

        if (this.configuration.accessToken) 
            authState = `(authenticated) `;
        
        return this.http
            .request<models.ApiUserProfile>(requestMethodStr, requestPath, { 
                observe: 'response', 
                responseType: 'json',
                headers, 
                withCredentials: this.configuration.withCredentials,
                body: body == null ? '' : JSON.stringify(body), // https://github.com/angular/angular/issues/10612
            })
            .pipe(
                catchError(e => {
                    let time = Date.now() - traceStart;

                    if (e instanceof HttpErrorResponse) {
                        let error = <HttpErrorResponse>e;
                        this.logger.info(`[API] ${requestMethodStr} ${authState}${requestPath} (${time} ms): Error: ${error.status} ${error.statusText}`);
                        e = error.error || e;
                    }

                    return throwError(e);
                }),
                map(response => {
                    let time = Date.now() - traceStart;
                    this.logger.info(`[API] ${requestMethodStr} ${authState}${requestPath} (${time} ms): Finished: ${response.status} ${response.statusText}`);

                    return response;
                })
            )
        ;
    }

    /**
     * 
     * 
     * @param body 
     */
    public upsertUserOmniauthRequest(body?: models.ApiPostUsersCurrentOmniauth, extraHttpRequestParams?: any): Observable<HttpResponse<models.ApiUserOmniauth>> {
        const path = this.basePath + '/users/current/omniauth';

        let traceStart = Date.now();
        let queryParameters : Record<string,string> = {};

        let globalQueryParams = (this.configuration.globalQueryParams || {});
        Object.keys(globalQueryParams).forEach (x => queryParameters[x] = globalQueryParams[x]);
        
        let headers : Record<string,string> = Object.assign({}, this.defaultHeaders); 

        this.applyGlobalHeaders(headers);

        headers['Content-Type'] = 'application/json';


        let requestMethodStr = 'RequestMethod.Post'.replace(/RequestMethod\./, '').toUpperCase();
        let requestPath = path;
        let authState = '';

        if (Object.keys(queryParameters).length > 0) {
            requestPath = `${requestPath}?${Object.keys(queryParameters)
                .map(key => `${encodeURIComponent(key)}=${encodeURIComponent(queryParameters[key])}`).join('&')}`;
        }

        if (this.configuration.accessToken) 
            authState = `(authenticated) `;
        
        return this.http
            .request<models.ApiUserOmniauth>(requestMethodStr, requestPath, { 
                observe: 'response', 
                responseType: 'json',
                headers, 
                withCredentials: this.configuration.withCredentials,
                body: body == null ? '' : JSON.stringify(body), // https://github.com/angular/angular/issues/10612
            })
            .pipe(
                catchError(e => {
                    let time = Date.now() - traceStart;

                    if (e instanceof HttpErrorResponse) {
                        let error = <HttpErrorResponse>e;
                        this.logger.info(`[API] ${requestMethodStr} ${authState}${requestPath} (${time} ms): Error: ${error.status} ${error.statusText}`);
                        e = error.error || e;
                    }

                    return throwError(e);
                }),
                map(response => {
                    let time = Date.now() - traceStart;
                    this.logger.info(`[API] ${requestMethodStr} ${authState}${requestPath} (${time} ms): Finished: ${response.status} ${response.statusText}`);

                    return response;
                })
            )
        ;
    }

    /**
     * 
     * 
     * @param body 
     */
    public spotSSORequest(body?: models.ApiPostUsersCurrentSpotim, extraHttpRequestParams?: any): Observable<HttpResponse<models.ApiSpotSSO>> {
        const path = this.basePath + '/users/current/spotim';

        let traceStart = Date.now();
        let queryParameters : Record<string,string> = {};

        let globalQueryParams = (this.configuration.globalQueryParams || {});
        Object.keys(globalQueryParams).forEach (x => queryParameters[x] = globalQueryParams[x]);
        
        let headers : Record<string,string> = Object.assign({}, this.defaultHeaders); 

        this.applyGlobalHeaders(headers);

        headers['Content-Type'] = 'application/json';


        let requestMethodStr = 'RequestMethod.Post'.replace(/RequestMethod\./, '').toUpperCase();
        let requestPath = path;
        let authState = '';

        if (Object.keys(queryParameters).length > 0) {
            requestPath = `${requestPath}?${Object.keys(queryParameters)
                .map(key => `${encodeURIComponent(key)}=${encodeURIComponent(queryParameters[key])}`).join('&')}`;
        }

        if (this.configuration.accessToken) 
            authState = `(authenticated) `;
        
        return this.http
            .request<models.ApiSpotSSO>(requestMethodStr, requestPath, { 
                observe: 'response', 
                responseType: 'json',
                headers, 
                withCredentials: this.configuration.withCredentials,
                body: body == null ? '' : JSON.stringify(body), // https://github.com/angular/angular/issues/10612
            })
            .pipe(
                catchError(e => {
                    let time = Date.now() - traceStart;

                    if (e instanceof HttpErrorResponse) {
                        let error = <HttpErrorResponse>e;
                        this.logger.info(`[API] ${requestMethodStr} ${authState}${requestPath} (${time} ms): Error: ${error.status} ${error.statusText}`);
                        e = error.error || e;
                    }

                    return throwError(e);
                }),
                map(response => {
                    let time = Date.now() - traceStart;
                    this.logger.info(`[API] ${requestMethodStr} ${authState}${requestPath} (${time} ms): Finished: ${response.status} ${response.statusText}`);

                    return response;
                })
            )
        ;
    }

    /**
     * 
     * 
     * @param body 
     */
    public spotAssociateUserRequest(body?: models.ApiPostUsersCurrentSpotimAssociate, extraHttpRequestParams?: any): Observable<HttpResponse<{}>> {
        const path = this.basePath + '/users/current/spotim/associate';

        let traceStart = Date.now();
        let queryParameters : Record<string,string> = {};

        let globalQueryParams = (this.configuration.globalQueryParams || {});
        Object.keys(globalQueryParams).forEach (x => queryParameters[x] = globalQueryParams[x]);
        
        let headers : Record<string,string> = Object.assign({}, this.defaultHeaders); 

        this.applyGlobalHeaders(headers);

        headers['Content-Type'] = 'application/json';


        let requestMethodStr = 'RequestMethod.Post'.replace(/RequestMethod\./, '').toUpperCase();
        let requestPath = path;
        let authState = '';

        if (Object.keys(queryParameters).length > 0) {
            requestPath = `${requestPath}?${Object.keys(queryParameters)
                .map(key => `${encodeURIComponent(key)}=${encodeURIComponent(queryParameters[key])}`).join('&')}`;
        }

        if (this.configuration.accessToken) 
            authState = `(authenticated) `;
        
        return this.http
            .request<any>(requestMethodStr, requestPath, { 
                observe: 'response', 
                responseType: 'json',
                headers, 
                withCredentials: this.configuration.withCredentials,
                body: body == null ? '' : JSON.stringify(body), // https://github.com/angular/angular/issues/10612
            })
            .pipe(
                catchError(e => {
                    let time = Date.now() - traceStart;

                    if (e instanceof HttpErrorResponse) {
                        let error = <HttpErrorResponse>e;
                        this.logger.info(`[API] ${requestMethodStr} ${authState}${requestPath} (${time} ms): Error: ${error.status} ${error.statusText}`);
                        e = error.error || e;
                    }

                    return throwError(e);
                }),
                map(response => {
                    let time = Date.now() - traceStart;
                    this.logger.info(`[API] ${requestMethodStr} ${authState}${requestPath} (${time} ms): Finished: ${response.status} ${response.statusText}`);

                    return response;
                })
            )
        ;
    }

    /**
     * 
     * 
     * @param body 
     */
    public mergeAccountsRequest(body?: models.ApiPostUsersMerge, extraHttpRequestParams?: any): Observable<HttpResponse<models.ApiUserAuthToken>> {
        const path = this.basePath + '/users/merge';

        let traceStart = Date.now();
        let queryParameters : Record<string,string> = {};

        let globalQueryParams = (this.configuration.globalQueryParams || {});
        Object.keys(globalQueryParams).forEach (x => queryParameters[x] = globalQueryParams[x]);
        
        let headers : Record<string,string> = Object.assign({}, this.defaultHeaders); 

        this.applyGlobalHeaders(headers);

        headers['Content-Type'] = 'application/json';


        let requestMethodStr = 'RequestMethod.Post'.replace(/RequestMethod\./, '').toUpperCase();
        let requestPath = path;
        let authState = '';

        if (Object.keys(queryParameters).length > 0) {
            requestPath = `${requestPath}?${Object.keys(queryParameters)
                .map(key => `${encodeURIComponent(key)}=${encodeURIComponent(queryParameters[key])}`).join('&')}`;
        }

        if (this.configuration.accessToken) 
            authState = `(authenticated) `;
        
        return this.http
            .request<models.ApiUserAuthToken>(requestMethodStr, requestPath, { 
                observe: 'response', 
                responseType: 'json',
                headers, 
                withCredentials: this.configuration.withCredentials,
                body: body == null ? '' : JSON.stringify(body), // https://github.com/angular/angular/issues/10612
            })
            .pipe(
                catchError(e => {
                    let time = Date.now() - traceStart;

                    if (e instanceof HttpErrorResponse) {
                        let error = <HttpErrorResponse>e;
                        this.logger.info(`[API] ${requestMethodStr} ${authState}${requestPath} (${time} ms): Error: ${error.status} ${error.statusText}`);
                        e = error.error || e;
                    }

                    return throwError(e);
                }),
                map(response => {
                    let time = Date.now() - traceStart;
                    this.logger.info(`[API] ${requestMethodStr} ${authState}${requestPath} (${time} ms): Finished: ${response.status} ${response.statusText}`);

                    return response;
                })
            )
        ;
    }

    /**
     * 
     * 
     * @param body 
     */
    public resetMyPasswordRequest(body?: models.ApiPostUsersPasswordReset, extraHttpRequestParams?: any): Observable<HttpResponse<{}>> {
        const path = this.basePath + '/users/password/reset';

        let traceStart = Date.now();
        let queryParameters : Record<string,string> = {};

        let globalQueryParams = (this.configuration.globalQueryParams || {});
        Object.keys(globalQueryParams).forEach (x => queryParameters[x] = globalQueryParams[x]);
        
        let headers : Record<string,string> = Object.assign({}, this.defaultHeaders); 

        this.applyGlobalHeaders(headers);

        headers['Content-Type'] = 'application/json';


        let requestMethodStr = 'RequestMethod.Post'.replace(/RequestMethod\./, '').toUpperCase();
        let requestPath = path;
        let authState = '';

        if (Object.keys(queryParameters).length > 0) {
            requestPath = `${requestPath}?${Object.keys(queryParameters)
                .map(key => `${encodeURIComponent(key)}=${encodeURIComponent(queryParameters[key])}`).join('&')}`;
        }

        if (this.configuration.accessToken) 
            authState = `(authenticated) `;
        
        return this.http
            .request<any>(requestMethodStr, requestPath, { 
                observe: 'response', 
                responseType: 'json',
                headers, 
                withCredentials: this.configuration.withCredentials,
                body: body == null ? '' : JSON.stringify(body), // https://github.com/angular/angular/issues/10612
            })
            .pipe(
                catchError(e => {
                    let time = Date.now() - traceStart;

                    if (e instanceof HttpErrorResponse) {
                        let error = <HttpErrorResponse>e;
                        this.logger.info(`[API] ${requestMethodStr} ${authState}${requestPath} (${time} ms): Error: ${error.status} ${error.statusText}`);
                        e = error.error || e;
                    }

                    return throwError(e);
                }),
                map(response => {
                    let time = Date.now() - traceStart;
                    this.logger.info(`[API] ${requestMethodStr} ${authState}${requestPath} (${time} ms): Finished: ${response.status} ${response.statusText}`);

                    return response;
                })
            )
        ;
    }

    /**
     * 
     * 
     * @param id 
     */
    public disconnectExternalAccountRequest(id?: string, extraHttpRequestParams?: any): Observable<HttpResponse<{}>> {
        const path = this.basePath + '/users/connected-accounts/{id}/disconnect'
                    .replace('{' + 'id' + '}', String(id));

        let traceStart = Date.now();
        let queryParameters : Record<string,string> = {};

        let globalQueryParams = (this.configuration.globalQueryParams || {});
        Object.keys(globalQueryParams).forEach (x => queryParameters[x] = globalQueryParams[x]);
        
        let headers : Record<string,string> = Object.assign({}, this.defaultHeaders); 

        this.applyGlobalHeaders(headers);


        let requestMethodStr = 'RequestMethod.Post'.replace(/RequestMethod\./, '').toUpperCase();
        let requestPath = path;
        let authState = '';

        if (Object.keys(queryParameters).length > 0) {
            requestPath = `${requestPath}?${Object.keys(queryParameters)
                .map(key => `${encodeURIComponent(key)}=${encodeURIComponent(queryParameters[key])}`).join('&')}`;
        }

        if (this.configuration.accessToken) 
            authState = `(authenticated) `;
        
        return this.http
            .request<any>(requestMethodStr, requestPath, { 
                observe: 'response', 
                responseType: 'json',
                headers, 
                withCredentials: this.configuration.withCredentials,
            })
            .pipe(
                catchError(e => {
                    let time = Date.now() - traceStart;

                    if (e instanceof HttpErrorResponse) {
                        let error = <HttpErrorResponse>e;
                        this.logger.info(`[API] ${requestMethodStr} ${authState}${requestPath} (${time} ms): Error: ${error.status} ${error.statusText}`);
                        e = error.error || e;
                    }

                    return throwError(e);
                }),
                map(response => {
                    let time = Date.now() - traceStart;
                    this.logger.info(`[API] ${requestMethodStr} ${authState}${requestPath} (${time} ms): Finished: ${response.status} ${response.statusText}`);

                    return response;
                })
            )
        ;
    }

    /**
     * 
     * 
     * @param userid 
     */
    public getPublicProfileRequest(userid?: string, extraHttpRequestParams?: any): Observable<HttpResponse<models.ApiPublicProfile>> {
        const path = this.basePath + '/users/{userid}'
                    .replace('{' + 'userid' + '}', String(userid));

        let traceStart = Date.now();
        let queryParameters : Record<string,string> = {};

        let globalQueryParams = (this.configuration.globalQueryParams || {});
        Object.keys(globalQueryParams).forEach (x => queryParameters[x] = globalQueryParams[x]);
        
        let headers : Record<string,string> = Object.assign({}, this.defaultHeaders); 

        this.applyGlobalHeaders(headers);


        let requestMethodStr = 'RequestMethod.Get'.replace(/RequestMethod\./, '').toUpperCase();
        let requestPath = path;
        let authState = '';

        if (Object.keys(queryParameters).length > 0) {
            requestPath = `${requestPath}?${Object.keys(queryParameters)
                .map(key => `${encodeURIComponent(key)}=${encodeURIComponent(queryParameters[key])}`).join('&')}`;
        }

        if (this.configuration.accessToken) 
            authState = `(authenticated) `;
        
        return this.http
            .request<models.ApiPublicProfile>(requestMethodStr, requestPath, { 
                observe: 'response', 
                responseType: 'json',
                headers, 
                withCredentials: this.configuration.withCredentials,
            })
            .pipe(
                catchError(e => {
                    let time = Date.now() - traceStart;

                    if (e instanceof HttpErrorResponse) {
                        let error = <HttpErrorResponse>e;
                        this.logger.info(`[API] ${requestMethodStr} ${authState}${requestPath} (${time} ms): Error: ${error.status} ${error.statusText}`);
                        e = error.error || e;
                    }

                    return throwError(e);
                }),
                map(response => {
                    let time = Date.now() - traceStart;
                    this.logger.info(`[API] ${requestMethodStr} ${authState}${requestPath} (${time} ms): Finished: ${response.status} ${response.statusText}`);

                    return response;
                })
            )
        ;
    }

    /**
     * 
     * 
     * @param userid 
     * @param body 
     */
    public reportProfileRequest(userid?: string, body?: models.ApiPostUsersUseridReport, extraHttpRequestParams?: any): Observable<HttpResponse<{}>> {
        const path = this.basePath + '/users/{userid}/report'
                    .replace('{' + 'userid' + '}', String(userid));

        let traceStart = Date.now();
        let queryParameters : Record<string,string> = {};

        let globalQueryParams = (this.configuration.globalQueryParams || {});
        Object.keys(globalQueryParams).forEach (x => queryParameters[x] = globalQueryParams[x]);
        
        let headers : Record<string,string> = Object.assign({}, this.defaultHeaders); 

        this.applyGlobalHeaders(headers);

        headers['Content-Type'] = 'application/json';


        let requestMethodStr = 'RequestMethod.Post'.replace(/RequestMethod\./, '').toUpperCase();
        let requestPath = path;
        let authState = '';

        if (Object.keys(queryParameters).length > 0) {
            requestPath = `${requestPath}?${Object.keys(queryParameters)
                .map(key => `${encodeURIComponent(key)}=${encodeURIComponent(queryParameters[key])}`).join('&')}`;
        }

        if (this.configuration.accessToken) 
            authState = `(authenticated) `;
        
        return this.http
            .request<any>(requestMethodStr, requestPath, { 
                observe: 'response', 
                responseType: 'json',
                headers, 
                withCredentials: this.configuration.withCredentials,
                body: body == null ? '' : JSON.stringify(body), // https://github.com/angular/angular/issues/10612
            })
            .pipe(
                catchError(e => {
                    let time = Date.now() - traceStart;

                    if (e instanceof HttpErrorResponse) {
                        let error = <HttpErrorResponse>e;
                        this.logger.info(`[API] ${requestMethodStr} ${authState}${requestPath} (${time} ms): Error: ${error.status} ${error.statusText}`);
                        e = error.error || e;
                    }

                    return throwError(e);
                }),
                map(response => {
                    let time = Date.now() - traceStart;
                    this.logger.info(`[API] ${requestMethodStr} ${authState}${requestPath} (${time} ms): Finished: ${response.status} ${response.statusText}`);

                    return response;
                })
            )
        ;
    }

    /**
     * Retrieve an authentication token for testing
     * 
     */
    public postUsersAuthTestRequest(extraHttpRequestParams?: any): Observable<HttpResponse<{}>> {
        const path = this.basePath + '/users/auth/test';

        let traceStart = Date.now();
        let queryParameters : Record<string,string> = {};

        let globalQueryParams = (this.configuration.globalQueryParams || {});
        Object.keys(globalQueryParams).forEach (x => queryParameters[x] = globalQueryParams[x]);
        
        let headers : Record<string,string> = Object.assign({}, this.defaultHeaders); 

        this.applyGlobalHeaders(headers);


        let requestMethodStr = 'RequestMethod.Post'.replace(/RequestMethod\./, '').toUpperCase();
        let requestPath = path;
        let authState = '';

        if (Object.keys(queryParameters).length > 0) {
            requestPath = `${requestPath}?${Object.keys(queryParameters)
                .map(key => `${encodeURIComponent(key)}=${encodeURIComponent(queryParameters[key])}`).join('&')}`;
        }

        if (this.configuration.accessToken) 
            authState = `(authenticated) `;
        
        return this.http
            .request<any>(requestMethodStr, requestPath, { 
                observe: 'response', 
                responseType: 'json',
                headers, 
                withCredentials: this.configuration.withCredentials,
            })
            .pipe(
                catchError(e => {
                    let time = Date.now() - traceStart;

                    if (e instanceof HttpErrorResponse) {
                        let error = <HttpErrorResponse>e;
                        this.logger.info(`[API] ${requestMethodStr} ${authState}${requestPath} (${time} ms): Error: ${error.status} ${error.statusText}`);
                        e = error.error || e;
                    }

                    return throwError(e);
                }),
                map(response => {
                    let time = Date.now() - traceStart;
                    this.logger.info(`[API] ${requestMethodStr} ${authState}${requestPath} (${time} ms): Finished: ${response.status} ${response.statusText}`);

                    return response;
                })
            )
        ;
    }

    /**
     * Retrieve a specific user&#39;s auth token for testing (development only)
     * 
     * @param id 
     */
    public postUsersAuthTestIdRequest(id?: number, extraHttpRequestParams?: any): Observable<HttpResponse<{}>> {
        const path = this.basePath + '/users/auth/test/{id}'
                    .replace('{' + 'id' + '}', String(id));

        let traceStart = Date.now();
        let queryParameters : Record<string,string> = {};

        let globalQueryParams = (this.configuration.globalQueryParams || {});
        Object.keys(globalQueryParams).forEach (x => queryParameters[x] = globalQueryParams[x]);
        
        let headers : Record<string,string> = Object.assign({}, this.defaultHeaders); 

        this.applyGlobalHeaders(headers);


        let requestMethodStr = 'RequestMethod.Post'.replace(/RequestMethod\./, '').toUpperCase();
        let requestPath = path;
        let authState = '';

        if (Object.keys(queryParameters).length > 0) {
            requestPath = `${requestPath}?${Object.keys(queryParameters)
                .map(key => `${encodeURIComponent(key)}=${encodeURIComponent(queryParameters[key])}`).join('&')}`;
        }

        if (this.configuration.accessToken) 
            authState = `(authenticated) `;
        
        return this.http
            .request<any>(requestMethodStr, requestPath, { 
                observe: 'response', 
                responseType: 'json',
                headers, 
                withCredentials: this.configuration.withCredentials,
            })
            .pipe(
                catchError(e => {
                    let time = Date.now() - traceStart;

                    if (e instanceof HttpErrorResponse) {
                        let error = <HttpErrorResponse>e;
                        this.logger.info(`[API] ${requestMethodStr} ${authState}${requestPath} (${time} ms): Error: ${error.status} ${error.statusText}`);
                        e = error.error || e;
                    }

                    return throwError(e);
                }),
                map(response => {
                    let time = Date.now() - traceStart;
                    this.logger.info(`[API] ${requestMethodStr} ${authState}${requestPath} (${time} ms): Finished: ${response.status} ${response.statusText}`);

                    return response;
                })
            )
        ;
    }

    /**
     * 
     * 
     * @param identifier 
     */
    public resolveSpotRequest(identifier?: number, extraHttpRequestParams?: any): Observable<HttpResponse<{}>> {
        const path = this.basePath + '/spot/{identifier}'
                    .replace('{' + 'identifier' + '}', String(identifier));

        let traceStart = Date.now();
        let queryParameters : Record<string,string> = {};

        let globalQueryParams = (this.configuration.globalQueryParams || {});
        Object.keys(globalQueryParams).forEach (x => queryParameters[x] = globalQueryParams[x]);
        
        let headers : Record<string,string> = Object.assign({}, this.defaultHeaders); 

        this.applyGlobalHeaders(headers);


        let requestMethodStr = 'RequestMethod.Get'.replace(/RequestMethod\./, '').toUpperCase();
        let requestPath = path;
        let authState = '';

        if (Object.keys(queryParameters).length > 0) {
            requestPath = `${requestPath}?${Object.keys(queryParameters)
                .map(key => `${encodeURIComponent(key)}=${encodeURIComponent(queryParameters[key])}`).join('&')}`;
        }

        if (this.configuration.accessToken) 
            authState = `(authenticated) `;
        
        return this.http
            .request<any>(requestMethodStr, requestPath, { 
                observe: 'response', 
                responseType: 'json',
                headers, 
                withCredentials: this.configuration.withCredentials,
            })
            .pipe(
                catchError(e => {
                    let time = Date.now() - traceStart;

                    if (e instanceof HttpErrorResponse) {
                        let error = <HttpErrorResponse>e;
                        this.logger.info(`[API] ${requestMethodStr} ${authState}${requestPath} (${time} ms): Error: ${error.status} ${error.statusText}`);
                        e = error.error || e;
                    }

                    return throwError(e);
                }),
                map(response => {
                    let time = Date.now() - traceStart;
                    this.logger.info(`[API] ${requestMethodStr} ${authState}${requestPath} (${time} ms): Finished: ${response.status} ${response.statusText}`);

                    return response;
                })
            )
        ;
    }

    /**
     * 
     * 
     * @param userid 
     */
    public translateSpotUserIDRequest(userid?: string, extraHttpRequestParams?: any): Observable<HttpResponse<models.ApiSpotUserLookupResult>> {
        const path = this.basePath + '/spot/users/{userid}'
                    .replace('{' + 'userid' + '}', String(userid));

        let traceStart = Date.now();
        let queryParameters : Record<string,string> = {};

        let globalQueryParams = (this.configuration.globalQueryParams || {});
        Object.keys(globalQueryParams).forEach (x => queryParameters[x] = globalQueryParams[x]);
        
        let headers : Record<string,string> = Object.assign({}, this.defaultHeaders); 

        this.applyGlobalHeaders(headers);


        let requestMethodStr = 'RequestMethod.Get'.replace(/RequestMethod\./, '').toUpperCase();
        let requestPath = path;
        let authState = '';

        if (Object.keys(queryParameters).length > 0) {
            requestPath = `${requestPath}?${Object.keys(queryParameters)
                .map(key => `${encodeURIComponent(key)}=${encodeURIComponent(queryParameters[key])}`).join('&')}`;
        }

        if (this.configuration.accessToken) 
            authState = `(authenticated) `;
        
        return this.http
            .request<models.ApiSpotUserLookupResult>(requestMethodStr, requestPath, { 
                observe: 'response', 
                responseType: 'json',
                headers, 
                withCredentials: this.configuration.withCredentials,
            })
            .pipe(
                catchError(e => {
                    let time = Date.now() - traceStart;

                    if (e instanceof HttpErrorResponse) {
                        let error = <HttpErrorResponse>e;
                        this.logger.info(`[API] ${requestMethodStr} ${authState}${requestPath} (${time} ms): Error: ${error.status} ${error.statusText}`);
                        e = error.error || e;
                    }

                    return throwError(e);
                }),
                map(response => {
                    let time = Date.now() - traceStart;
                    this.logger.info(`[API] ${requestMethodStr} ${authState}${requestPath} (${time} ms): Finished: ${response.status} ${response.statusText}`);

                    return response;
                })
            )
        ;
    }

    /**
     * 
     * 
     */
    public getOpenSessionsRequest(extraHttpRequestParams?: any): Observable<HttpResponse<PagedArray<models.ApiPlaybackSession>>> {
        const path = this.basePath + '/content/playback/open_sessions';

        let traceStart = Date.now();
        let queryParameters : Record<string,string> = {};

        let globalQueryParams = (this.configuration.globalQueryParams || {});
        Object.keys(globalQueryParams).forEach (x => queryParameters[x] = globalQueryParams[x]);
        
        let headers : Record<string,string> = Object.assign({}, this.defaultHeaders); 

        this.applyGlobalHeaders(headers);


        let requestMethodStr = 'RequestMethod.Get'.replace(/RequestMethod\./, '').toUpperCase();
        let requestPath = path;
        let authState = '';

        if (Object.keys(queryParameters).length > 0) {
            requestPath = `${requestPath}?${Object.keys(queryParameters)
                .map(key => `${encodeURIComponent(key)}=${encodeURIComponent(queryParameters[key])}`).join('&')}`;
        }

        if (this.configuration.accessToken) 
            authState = `(authenticated) `;
        
        return this.http
            .request<PagedArray<models.ApiPlaybackSession>>(requestMethodStr, requestPath, { 
                observe: 'response', 
                responseType: 'json',
                headers, 
                withCredentials: this.configuration.withCredentials,
            })
            .pipe(
                catchError(e => {
                    let time = Date.now() - traceStart;

                    if (e instanceof HttpErrorResponse) {
                        let error = <HttpErrorResponse>e;
                        this.logger.info(`[API] ${requestMethodStr} ${authState}${requestPath} (${time} ms): Error: ${error.status} ${error.statusText}`);
                        e = error.error || e;
                    }

                    return throwError(e);
                }),
                map(response => {
                    let time = Date.now() - traceStart;
                    this.logger.info(`[API] ${requestMethodStr} ${authState}${requestPath} (${time} ms): Finished: ${response.status} ${response.statusText}`);

                    return response;
                })
            )
        ;
    }

}
